import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { useInstallments } from '../hooks/useInstallments';
import { api } from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MdScale, MdTrendingUp, MdTrendingDown, MdPercent } from 'react-icons/md';
import { MdPerson, MdAttachFile, MdWarning, MdCompareArrows } from 'react-icons/md';
import { getLocalDateInputValue, toTitleCase } from '../utils/dateUtils';
import { getPatientDetailsPath, isValidPatientDetailTab } from '../utils/patientNavigation';

const FALLBACK_TIME_SLOTS = [
  '08:00',
  '08:30',
  '09:00',
  '09:30',
  '10:00',
  '10:30',
  '11:00',
  '11:30',
  '13:00',
  '13:30',
  '14:00',
  '14:30',
  '15:00',
  '15:30',
  '16:00',
  '16:30',
  '17:00',
  '17:30',
];

const DAY_OF_WEEK_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const parseTimeToMinutes = (value = '') => {
  const [hoursStr, minutesStr] = String(value).split(':');
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
};

const formatMinutesToTime = (minutes) => {
  const totalMinutes = Number(minutes);
  if (Number.isNaN(totalMinutes)) return '00:00';
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const formatCurrencyInput = (value = '') => {
  const digits = value.replace(/\D/g, '');
  const number = parseFloat(digits) / 100 || 0;
  return number.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseAmountToNumber = (value = '') => {
  if (typeof value === 'number') return value;
  return parseFloat(String(value).replace(/\./g, '').replace(',', '.')) || 0;
};

const formatDateForInput = (date) => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeDateToISO = (value) => {
  if (!value) return undefined;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day).toISOString();
};

const formatDateLabel = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR').format(date);
};

const formatPercentage = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value.toFixed(1)}%`;
};

const formatWeightValue = (value) => {
  if (value === null || value === undefined) return null;
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return null;
  const trimmed = numericValue.toFixed(3).replace(/\.?0+$/, '');
  return trimmed.replace('.', ',');
};

const formatWeightLabel = (value) => {
  const formatted = formatWeightValue(value);
  return formatted === null ? '—' : `${formatted} kg`;
};

const formatQuantityValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return String(value).replace('.', ',');
  }
  return numericValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
};

const formatQuantityLabel = (value, unit = '') => {
  const formatted = formatQuantityValue(value);
  if (formatted === null) return '—';
  return `${formatted} ${unit || ''}`.trim();
};

const formatAmountForInputValue = (value = 0) => {
  const safe = Number(value) || 0;
  return safe.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const parseLocalizedNumber = (value) => {
  if (value === undefined || value === null) return null;
  const stringValue = String(value).trim();
  if (!stringValue) return null;
  const normalized = stringValue.includes(',')
    ? stringValue.replace(/\./g, '').replace(',', '.')
    : stringValue;
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const complementaryRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))',
  gap: '0.5rem',
  alignItems: 'center',
};

const defaultFinancialFormState = () => ({
  description: '',
  category: 'Consulta',
  amount: '',
  date: getLocalDateInputValue(),
  paymentStatus: 'pendente',
  paymentMethod: 'dinheiro',
  hasInstallments: false,
  installments: 1,
  installmentPaymentMethod: 'cartao_credito',
  notes: '',
  quantity: '',
});

const defaultConsultationForm = () => {
  const now = new Date();
  const h = String(now.getHours()).padStart(2, '0');
  const m = String(now.getMinutes()).padStart(2, '0');
  return {
    date: getLocalDateInputValue(),
    time: `${h}:${m}`,
    abdominal: '',
    weight: '',
    notes: '',
    protocol: '',
  };
};

const emptyProductRow = () => ({
  _rowId: `${Date.now()}-${Math.random()}`,
  query: '',
  selectedMedication: null,
  quantity: '',
  showDropdown: false,
});

const ConsultModalDeltaRow = ({ delta, unit, prev }) => {
  if (delta === null || isNaN(delta)) {
    return (
      <div style={{ marginTop: 5, fontSize: 11, color: '#8E8E93', letterSpacing: '-0.005em' }}>
        {prev ? `anterior ${prev}` : ''}
      </div>
    );
  }
  const isFlat = Math.abs(delta) < 0.05;
  const isGood = !isFlat && delta < 0;
  const color = isFlat ? '#8E8E93' : isGood ? '#3D8068' : '#9F4A60';
  const bg = isFlat ? '#F2F2F7' : isGood ? '#E6F1EB' : '#FBE0E5';
  const sign = delta > 0 ? '+' : '';
  return (
    <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center',
        fontSize: 10.5, fontWeight: 600, color, background: bg,
        padding: '2px 7px', borderRadius: 999,
        fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.005em',
        whiteSpace: 'nowrap',
      }}>
        {sign}{delta.toFixed(1).replace('.', ',')} {unit}
      </span>
    </div>
  );
};

export default function PacienteDetalhes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const patientFromState = location.state;
  const { onUpdatePatient, onRefreshPatient, patients, settings } = useOutletContext();
  const patientIdFromQuery = searchParams.get('id');
  const tabFromQuery = searchParams.get('tab');
  const initialTab = isValidPatientDetailTab(tabFromQuery) ? tabFromQuery : 'dashboard';
  const patientFromList = useMemo(
    () => (Array.isArray(patients) ? patients.find((entry) => String(entry?._id) === String(patientIdFromQuery)) : null),
    [patientIdFromQuery, patients]
  );
  const patient = patientFromList || patientFromState || null;
  const [resolvedPatient, setResolvedPatient] = useState(patient);
  const [currentPatient, setCurrentPatient] = useState(patient);
        // Calcula idade detalhada: XX anos, XX meses, XX dias
        function getDetailedAge(birthDate) {
          if (!birthDate) return '—';
          const now = new Date();
          const birth = new Date(birthDate);
          let years = now.getFullYear() - birth.getFullYear();
          let months = now.getMonth() - birth.getMonth();
          let days = now.getDate() - birth.getDate();
          if (days < 0) {
            months--;
            const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
            days += prevMonth.getDate();
          }
          if (months < 0) {
            years--;
            months += 12;
          }
          return `${years} anos, ${months} meses e ${days} dias`;
        }
  const [draftPatient, setDraftPatient] = useState(patient);
  const [editingMain, setEditingMain] = useState(false);
  const [editingComplementary, setEditingComplementary] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [financialLoading, setFinancialLoading] = useState(false);
  const [financialTransactions, setFinancialTransactions] = useState([]);
  const [financialError, setFinancialError] = useState('');
  const [showFinancialModal, setShowFinancialModal] = useState(false);
  const [financialForm, setFinancialForm] = useState(defaultFinancialFormState);
  const [medications, setMedications] = useState([]);
  const [medicationQuery, setMedicationQuery] = useState('');
  const [showMedicationDropdown, setShowMedicationDropdown] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [installmentSchedule, setInstallmentSchedule] = useState([]);
  const revenueCategories = ['Consulta', 'Procedimento', 'Exame', 'Produto', 'Outro'];
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const [consultationForm, setConsultationForm] = useState(defaultConsultationForm());
  const [consultationProducts, setConsultationProducts] = useState(() => [emptyProductRow()]);
  const [editingConsultationId, setEditingConsultationId] = useState(null);
  const [editingConsultation, setEditingConsultation] = useState(null);
  const [consultationAttachmentFile, setConsultationAttachmentFile] = useState(null);
  const [consultationAttachmentKey, setConsultationAttachmentKey] = useState(null);
  const [consultationAttachmentPreviewUrl, setConsultationAttachmentPreviewUrl] = useState(null);
  const [consultSort, setConsultSort] = useState({ key: 'date', dir: 'desc' });
  const [displayedInitialWeight, setDisplayedInitialWeight] = useState(0);
  const [displayedLastConsultWeight, setDisplayedLastConsultWeight] = useState(0);
  const [displayedWeightLossTotal, setDisplayedWeightLossTotal] = useState(0);
  const [displayedTotalWeightLossPercent, setDisplayedTotalWeightLossPercent] = useState(0);
  const [displayedWeeklyWeightLoss, setDisplayedWeeklyWeightLoss] = useState(0);
  const [displayedAbdominalLoss, setDisplayedAbdominalLoss] = useState(0);
  const installmentHook = useInstallments(financialForm, parseAmountToNumber, formatDateForInput);
  const { installmentSchedule: hookSchedule, handleInstallmentFieldChange, recalculateInstallments, unlockSchedule, overrideSchedule } = installmentHook;
  const [chartKey, setChartKey] = useState(0);
  const scheduleSettings = settings?.scheduleSettings || null;

  useEffect(() => {
    setResolvedPatient(patient);
  }, [patient]);

  useEffect(() => {
    if (!patientIdFromQuery || patient?._id) return;

    let isMounted = true;

    api.getPatient(patientIdFromQuery)
      .then((data) => {
        if (isMounted && data?._id) {
          setResolvedPatient(data);
        }
      })
      .catch((error) => {
        console.error('Erro ao carregar paciente pelos parâmetros da rota', error);
      });

    return () => {
      isMounted = false;
    };
  }, [patient?._id, patientIdFromQuery]);

  useEffect(() => {
    const nextTab = isValidPatientDetailTab(tabFromQuery) ? tabFromQuery : 'dashboard';
    setActiveTab((currentTab) => (currentTab === nextTab ? currentTab : nextTab));
  }, [tabFromQuery]);

  useEffect(() => {
    const effectivePatientId = resolvedPatient?._id || patientIdFromQuery;
    const nextPath = getPatientDetailsPath(effectivePatientId, activeTab);
    const currentPath = `${location.pathname}${location.search}`;

    if (effectivePatientId && currentPath !== nextPath) {
      navigate(nextPath, { replace: true, state: resolvedPatient || patientFromState || undefined });
    }
  }, [activeTab, location.pathname, location.search, navigate, patientFromState, patientIdFromQuery, resolvedPatient]);

  useEffect(() => {
    const interval = setInterval(() => {
      setChartKey(prev => prev + 1);
    }, 5000); // Reanima a cada 5 segundos
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    setInstallmentSchedule(hookSchedule);
  }, [hookSchedule]);
  const scheduleCount = financialForm.hasInstallments
    ? installmentSchedule.length || financialForm.installments
    : 0;
  const totalAmountValue = parseAmountToNumber(financialForm.amount || '0');
  const totalInstallmentAmount = installmentSchedule.reduce((sum, inst) => {
    if (!inst.amount) return sum;
    return sum + parseAmountToNumber(inst.amount);
  }, 0);
  const differenceAmount = totalAmountValue - totalInstallmentAmount;
  const medicationSuggestions = useMemo(() => {
    const query = medicationQuery.trim().toLowerCase();
    if (!query) return medications.slice(0, 6);
    return medications
      .filter((med) => {
        const name = med.name?.toLowerCase() || '';
        const lab = med.laboratory?.toLowerCase() || '';
        return name.includes(query) || lab.includes(query);
      })
      .slice(0, 6);
  }, [medications, medicationQuery]);

  const formatFullAddress = (data) => {
    if (!data) return '';
    const main = [data.address, data.number].filter(Boolean).join(', ');
    const extras = [data.complement, data.district].filter(Boolean).join(', ');
    const cityState = [data.city, data.state].filter(Boolean).join(' / ');
    const zip = data.zipCode ? `CEP: ${data.zipCode}` : '';
    return [main, extras, cityState, zip].filter(Boolean).join(' • ');
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return '—';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return `${age} anos`;
  };

  const calculateIMC = (weight, height) => {
    if (!weight || !height) return '—';
    const weightNum = parseFloat(weight);
    const heightNum = parseFloat(height) / 100; // converter cm para metros
    if (weightNum <= 0 || heightNum <= 0) return '—';
    const imc = weightNum / (heightNum * heightNum);
    return imc.toFixed(1);
  };

  const listItemStyle = { display: 'flex', flexDirection: 'column', gap: '0.25rem', width: '100%' };
  const fullWidthInputStyle = { width: '100%' };

  useEffect(() => {
    setCurrentPatient(resolvedPatient);
    setDraftPatient(resolvedPatient);
    setEditingMain(false);
    setEditingComplementary(false);
    if (resolvedPatient?._id) {
      fetchFinancialTransactions(resolvedPatient._id);
      fetchConsultations(resolvedPatient._id);
    } else {
      setFinancialTransactions([]);
      setConsultations([]);
    }
  }, [resolvedPatient]);

  const resetConsultationForm = () => {
    const now = new Date();
    setConsultationForm({
      date: now.toLocaleDateString('en-CA'),
      time: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
      abdominal: '',
      weight: '',
      notes: '',
      protocol: '',
    });
    setConsultationProducts([emptyProductRow()]);
    setConsultationAttachmentFile(null);
    setConsultationAttachmentKey(null);
    setConsultationAttachmentPreviewUrl(null);
  };

  const resetFinancialForm = () => {
    setFinancialForm(defaultFinancialFormState());
    setSelectedMedication(null);
    setMedicationQuery('');
    setShowMedicationDropdown(false);
    setEditingTransactionId(null);
  };

  const fetchFinancialTransactions = async (patientId) => {
    try {
      setFinancialLoading(true);
      setFinancialError('');
      const { data } = await api.get(`/financial?patientId=${patientId}`);
      setFinancialTransactions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar movimentações financeiras', error);
      setFinancialError('Não foi possível carregar as movimentações financeiras.');
    } finally {
      setFinancialLoading(false);
    }
  };

  const fetchConsultations = async (patientId) => {
    try {
      const { data } = await api.get(`/patients/${patientId}/consultations`);
      if (Array.isArray(data)) {
        setConsultations(
          data.map((entry) => ({
            ...entry,
            productLab: entry.laboratory || entry.productLab,
          }))
        );
      } else {
        setConsultations([]);
      }
    } catch (error) {
      console.error('Erro ao carregar consultas do paciente', error);
      setConsultations([]);
    }
  };

  const loadMedications = useCallback(async () => {
    try {
      const { data } = await api.get('/inventory/medications');
      setMedications(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar medicamentos', error);
    }
  }, []);

  useEffect(() => {
    loadMedications();
  }, [loadMedications]);


  const formatCurrency = (value = 0) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('pt-BR').format(date);
  };

  const getStatusColor = (status) => {
    const colors = {
      pago: '#22c55e',
      pendente: '#f59e0b',
      atrasado: '#ef4444',
    };
    return colors[status] || '#6b7280';
  };

  const getTimeSlotsForDate = useCallback(
    (dateStr) => {
      if (!dateStr) return FALLBACK_TIME_SLOTS;
      const parsedDate = new Date(`${dateStr}T00:00:00`);
      if (Number.isNaN(parsedDate.getTime())) return FALLBACK_TIME_SLOTS;
      if (!scheduleSettings) return FALLBACK_TIME_SLOTS;

      const dayKey = DAY_OF_WEEK_KEYS[parsedDate.getDay()];
      const dayConfig = scheduleSettings.workDays?.[dayKey];
      if (!dayConfig || dayConfig.enabled === false) {
        return [];
      }

      const startTime = dayConfig.startTime || scheduleSettings.startTime || '08:00';
      const endTime = dayConfig.endTime || scheduleSettings.endTime || '18:00';
      const slotDuration = scheduleSettings.slotDuration || 30;
      const breakTimes = Array.isArray(dayConfig.breakTimes) ? dayConfig.breakTimes : [];
      const startMinutes = parseTimeToMinutes(startTime);
      const endMinutes = parseTimeToMinutes(endTime);
      if (startMinutes === null || endMinutes === null || slotDuration <= 0) {
        return FALLBACK_TIME_SLOTS;
      }

      const slots = [];
      let currentMinutes = startMinutes;
      let guard = 0;
      while (currentMinutes < endMinutes && guard < 500) {
        guard += 1;
        const currentBreak = breakTimes.find((breakTime) => {
          const breakStart = parseTimeToMinutes(breakTime.startTime);
          const breakEnd = parseTimeToMinutes(breakTime.endTime);
          if (breakStart === null || breakEnd === null) return false;
          return currentMinutes >= breakStart && currentMinutes < breakEnd;
        });
        if (currentBreak) {
          const breakEndMinutes = parseTimeToMinutes(currentBreak.endTime);
          if (breakEndMinutes === null || breakEndMinutes <= currentMinutes) {
            break;
          }
          currentMinutes = breakEndMinutes;
          continue;
        }
        slots.push(formatMinutesToTime(currentMinutes));
        currentMinutes += slotDuration;
      }
      return slots.length ? slots : FALLBACK_TIME_SLOTS;
    },
    [scheduleSettings]
  );

  const createPresenceAppointmentForConsultation = useCallback(
    async (consultationDate, consultationTime) => {
      if (!currentPatient?._id) return;
      const targetDate = consultationDate || getLocalDateInputValue();
      const configuredSlots = getTimeSlotsForDate(targetDate);
      const slots = configuredSlots.length ? configuredSlots : FALLBACK_TIME_SLOTS;

      const now = new Date();
      const targetMinutes = (consultationTime ? parseTimeToMinutes(consultationTime) : null)
        ?? (now.getHours() * 60 + now.getMinutes());
      let preferredIndex = 0;
      let bestDiff = Infinity;
      slots.forEach((slot, index) => {
        const minutes = parseTimeToMinutes(slot);
        if (minutes === null) return;
        const diff = Math.abs(minutes - targetMinutes);
        if (diff < bestDiff) {
          bestDiff = diff;
          preferredIndex = index;
        } else if (diff === bestDiff) {
          // Preferir horários anteriores em empates
          const currentPreferred = parseTimeToMinutes(slots[preferredIndex]);
          if (currentPreferred !== null && minutes < currentPreferred) {
            preferredIndex = index;
          }
        }
      });
      if (bestDiff === Infinity) {
        preferredIndex = 0;
      }

      const orderToTry = [];
      for (let i = preferredIndex; i < slots.length; i += 1) {
        orderToTry.push(slots[i]);
      }
      for (let i = preferredIndex - 1; i >= 0; i -= 1) {
        orderToTry.push(slots[i]);
      }

      let dayAppointments = [];
      try {
        const { data } = await api.get(`/appointments?date=${targetDate}`);
        dayAppointments = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('Erro ao carregar agendamentos existentes', error);
      }
      const patientAppointments = dayAppointments.filter(
        (appointment) => String(appointment.patientId?._id || appointment.patientId) === String(currentPatient._id)
      );
      const MIN_INTERVAL_MINUTES = 180;
      const scheduledEntry = patientAppointments.find((appointment) => appointment.status === 'scheduled');
      if (scheduledEntry) {
        await api.put(`/appointments/${scheduledEntry._id}`, { status: 'present' });
        return scheduledEntry.time;
      }
      const recentPresent = patientAppointments.find((appointment) => {
        const appointmentMinutes = parseTimeToMinutes(appointment.time);
        if (appointmentMinutes === null) return false;
        if (!['present', 'confirmed'].includes(appointment.status)) return false;
        return Math.abs(appointmentMinutes - targetMinutes) < MIN_INTERVAL_MINUTES;
      });
      if (recentPresent) {
        if (recentPresent.status !== 'present') {
          await api.put(`/appointments/${recentPresent._id}`, { status: 'present' });
        }
        return recentPresent.time;
      }
      const occupiedSlots = new Set(dayAppointments.map((appointment) => appointment.time));
      let lastError = null;
      for (const time of orderToTry) {
        if (occupiedSlots.has(time)) continue;
        try {
          const { data: createdAppointment } = await api.post('/appointments', {
            date: targetDate,
            time,
            patientId: currentPatient._id,
            patientName: currentPatient.name || 'Paciente',
            type: 'Consulta',
          });
          if (createdAppointment?._id) {
            await api.put(`/appointments/${createdAppointment._id}`, { status: 'present' });
          }
          console.log('[Agenda] Agendamento criado → data:', targetDate, '| horário:', time);
          return time;
        } catch (error) {
          lastError = error;
          const message = error?.response?.data?.message || '';
          if (message.includes('Já existe um agendamento')) {
            occupiedSlots.add(time);
            continue;
          }
          throw error;
        }
      }
      if (lastError) {
        throw lastError;
      }
      console.warn('[Agenda] Nenhum horário disponível para', targetDate, '— slots tentados:', orderToTry);
    },
    [currentPatient, getTimeSlotsForDate]
  );

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    const normalized = name === 'name' ? toTitleCase(value) : value;
    setDraftPatient((prev) => ({ ...prev, [name]: normalized }));
  };

  const handleSave = (card) => {
    const updatedPatient = { ...draftPatient };
    setCurrentPatient(updatedPatient);
    setDraftPatient(updatedPatient);
    if (onUpdatePatient) onUpdatePatient(updatedPatient);
    if (card === 'main') setEditingMain(false);
    if (card === 'complementary') setEditingComplementary(false);
  };

  const handleCancel = (card) => {
    setDraftPatient(currentPatient);
    if (card === 'main') setEditingMain(false);
    if (card === 'complementary') setEditingComplementary(false);
  };

  const handleFinancialFormChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === 'hasInstallments') {
      setFinancialForm((prev) => ({
        ...prev,
        hasInstallments: checked,
        installments: checked ? prev.installments || 1 : 1,
      }));
      unlockSchedule();
      return;
    }

    if (name === 'installments') {
      const parsed = Math.max(1, Math.min(12, parseInt(value, 10) || 1));
      setFinancialForm((prev) => ({ ...prev, installments: parsed }));
      unlockSchedule();
      return;
    }

    if (name === 'installmentPaymentMethod') {
      setFinancialForm((prev) => ({ ...prev, installmentPaymentMethod: value }));
      unlockSchedule();
      return;
    }

    if (name === 'date') {
      setFinancialForm((prev) => ({ ...prev, date: value }));
      unlockSchedule();
      return;
    }

    if (name === 'category') {
      if (value !== 'Produto') {
        setSelectedMedication(null);
        setMedicationQuery('');
        setShowMedicationDropdown(false);
        setFinancialForm((prev) => ({ ...prev, quantity: '' }));
      }
      setFinancialForm((prev) => ({ ...prev, category: value }));
      return;
    }

    if (name === 'quantity') {
      const sanitized = value.replace(/\D/g, '');
      setFinancialForm((prev) => ({ ...prev, quantity: sanitized }));
      return;
    }

    setFinancialForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleAmountChange = (event) => {
    const formatted = formatCurrencyInput(event.target.value);
    setFinancialForm((prev) => ({ ...prev, amount: formatted }));
    unlockSchedule();
  };

  const handleMedicationInput = (event) => {
    const value = event.target.value;
    setMedicationQuery(value);
    setShowMedicationDropdown(true);
    setSelectedMedication(null);
    setFinancialForm((prev) => {
      if (prev.category === 'Produto' && !prev.description) {
        return { ...prev, description: value };
      }
      return prev;
    });
  };

  const handleSelectMedication = (medication) => {
    setSelectedMedication(medication);
    setMedicationQuery(medication.name || '');
    setShowMedicationDropdown(false);
    setFinancialForm((prev) => {
      if (!prev.description?.trim()) {
        return { ...prev, description: medication.name || '' };
      }
      return prev;
    });
  };

  const handleOpenBiocompare = async () => {
    const withAttachment = consultations
      .filter(c => c.attachmentUrl)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    if (withAttachment.length === 0) {
      alert('Nenhuma consulta com anexo encontrada.');
      return;
    }
    const first = withAttachment[0];
    const last = withAttachment[withAttachment.length - 1];
    const sameFile = first._id === last._id;
    try {
      const keys = sameFile
        ? [first.attachmentUrl]
        : [first.attachmentUrl, last.attachmentUrl];
      const responses = await Promise.all(
        keys.map(key => api.get(`/upload/signed-url?key=${encodeURIComponent(key)}`))
      );
      const firstUrl = responses[0].data.url;
      const lastUrl = responses[1] ? responses[1].data.url : firstUrl;
      const fmtDate = (d) => new Date(d).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
      const patientName = toTitleCase(patient?.name || '');
      const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Comparação de Bioimpedância${patientName ? ' — ' + patientName : ''}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{background:#F5F5F7;font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display','SF Pro Text',sans-serif;display:flex;flex-direction:column;height:100vh;overflow:hidden;color:#1C1C1E;-webkit-font-smoothing:antialiased}
header{background:#fff;padding:16px 24px;border-bottom:1px solid #E5E5EA;flex-shrink:0;display:flex;align-items:center;justify-content:space-between;box-shadow:0 1px 2px rgba(28,28,30,0.04)}
.header-left{}
.badge{display:inline-flex;align-items:center;gap:6px;background:#EFE3F0;color:#7A4A86;padding:4px 10px;border-radius:999px;font-size:11px;font-weight:600;letter-spacing:-0.005em;margin-bottom:8px}
.badge-dot{width:5px;height:5px;border-radius:50%;background:#7A4A86}
.header-left h1{font-size:20px;font-weight:700;letter-spacing:-0.025em;color:#1C1C1E;margin:0}
.header-left .subtitle{font-size:13px;color:#6E6E73;margin-top:4px;letter-spacing:-0.005em}
.grid{display:grid;grid-template-columns:${sameFile ? '1fr' : '1fr 1fr'};gap:14px;padding:14px;flex:1;min-height:0}
.card{display:flex;flex-direction:column;border-radius:18px;border:1px solid #E5E5EA;overflow:hidden;background:#fff;box-shadow:0 1px 2px rgba(28,28,30,0.04),0 8px 24px -16px rgba(28,28,30,0.14);min-height:0}
.card-header{padding:14px 18px;border-bottom:1px solid rgba(28,28,30,0.06);flex-shrink:0}
.card-header.first{background:linear-gradient(135deg,#F5F5F7 0%,#E8E8ED 100%)}
.card-header.last{background:linear-gradient(135deg,#FBE0E5 0%,#F4C4CE 100%)}
.card-label{font-size:10px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:#6E6E73;margin-bottom:4px}
.card-label.accent{color:#9F4A60}
.card-date{font-size:18px;font-weight:700;letter-spacing:-0.025em;color:#1C1C1E;font-variant-numeric:tabular-nums}
.img-wrap{flex:1;min-height:0;overflow:hidden;background:#FAFAFB;position:relative;cursor:grab;user-select:none}
.img-wrap:active{cursor:grabbing}
.img-wrap img{position:absolute;top:0;left:0;transform-origin:0 0;will-change:transform;pointer-events:none}
</style>
</head>
<body>
<header>
  <div class="header-left">
    <div class="badge"><span class="badge-dot"></span>Comparação de bioimpedância</div>
    <h1>${patientName || 'Paciente'}</h1>
    <div class="subtitle">${fmtDate(first.date)} → ${fmtDate(last.date)}${sameFile ? ' — apenas um registro encontrado' : ''}</div>
  </div>
</header>
<div class="grid">
  <div class="card">
    <div class="card-header first">
      <div class="card-label">Primeiro registro</div>
      <div class="card-date">${fmtDate(first.date)}</div>
    </div>
    <div class="img-wrap" id="w0"><img id="img0" src="${firstUrl}" alt="Primeiro registro"/></div>
  </div>
  ${!sameFile ? `<div class="card">
    <div class="card-header last">
      <div class="card-label accent">Último registro</div>
      <div class="card-date">${fmtDate(last.date)}</div>
    </div>
    <div class="img-wrap" id="w1"><img id="img1" src="${lastUrl}" alt="Último registro"/></div>
  </div>` : ''}
</div>
<script>
(function(){
  var panels=[];
  var wids=${sameFile ? "['w0']" : "['w0','w1']"};
  wids.forEach(function(id,i){panels.push({wrap:document.getElementById(id),img:document.getElementById('img'+i)});});

  // Shared transform state — applied equally to all panels
  var s=1,tx=0,ty=0,minS=0.1,maxS=8;
  var dragging=false,lx=0,ly=0,didDrag=false;

  function applyAll(){
    panels.forEach(function(p){p.img.style.transform='matrix('+s+',0,0,'+s+','+tx+','+ty+')';});
  }
  function clamp(){
    var p0=panels[0],ww=p0.wrap.clientWidth,wh=p0.wrap.clientHeight;
    var iw=p0.img.naturalWidth*s,ih=p0.img.naturalHeight*s;
    if(iw<=ww)tx=(ww-iw)/2; else tx=Math.min(0,Math.max(ww-iw,tx));
    if(ih<=wh)ty=(wh-ih)/2; else ty=Math.min(0,Math.max(wh-ih,ty));
  }
  function fit(){
    var ww=panels[0].wrap.clientWidth,wh=panels[0].wrap.clientHeight;
    var ns=Infinity;
    panels.forEach(function(p){if(p.img.naturalWidth)ns=Math.min(ns,Math.min(ww/p.img.naturalWidth,wh/p.img.naturalHeight));});
    if(ns===Infinity)return;
    minS=ns; s=ns;
    var p0=panels[0];
    tx=(ww-p0.img.naturalWidth*s)/2;
    ty=(wh-p0.img.naturalHeight*s)/2;
    applyAll();
  }
  panels.forEach(function(p){p.img.onload=fit;});
  new ResizeObserver(fit).observe(panels[0].wrap);

  // Wheel zoom — synced
  panels.forEach(function(p){
    p.wrap.addEventListener('wheel',function(e){
      e.preventDefault();
      var r=p.wrap.getBoundingClientRect(),cx=e.clientX-r.left,cy=e.clientY-r.top;
      var f=e.deltaY<0?1.06:1/1.06,ns=Math.max(minS,Math.min(maxS,s*f));
      tx=cx-(cx-tx)*(ns/s);ty=cy-(cy-ty)*(ns/s);s=ns;clamp();applyAll();
    },{passive:false});
  });

  // Drag — synced: start on any panel, move applies to both
  panels.forEach(function(p){
    p.wrap.addEventListener('mousedown',function(e){
      dragging=true;didDrag=false;lx=e.clientX;ly=e.clientY;
      document.body.style.cursor='grabbing';
    });
  });
  window.addEventListener('mousemove',function(e){
    if(!dragging)return;
    var dx=e.clientX-lx,dy=e.clientY-ly;
    if(Math.abs(dx)>3||Math.abs(dy)>3)didDrag=true;
    tx+=dx;ty+=dy;lx=e.clientX;ly=e.clientY;clamp();applyAll();
  });
  window.addEventListener('mouseup',function(){
    dragging=false;
    document.body.style.cursor='';
  });

  // Click (no drag) = toggle 2x zoom — synced
  panels.forEach(function(p){
    p.wrap.addEventListener('click',function(e){
      if(didDrag)return;
      var r=p.wrap.getBoundingClientRect(),cx=e.clientX-r.left,cy=e.clientY-r.top;
      if(s>minS+0.01){
        s=minS;
        var p0=panels[0];
        tx=(p0.wrap.clientWidth-p0.img.naturalWidth*s)/2;
        ty=(p0.wrap.clientHeight-p0.img.naturalHeight*s)/2;
      } else {
        var ns=Math.min(maxS,minS*2);
        tx=cx-(cx-tx)*(ns/s);ty=cy-(cy-ty)*(ns/s);s=ns;clamp();
      }
      applyAll();
    });
  });

  // Touch — synced
  panels.forEach(function(p){
    p.wrap.addEventListener('touchstart',function(e){
      if(e.touches.length===1){dragging=true;didDrag=false;lx=e.touches[0].clientX;ly=e.touches[0].clientY;}
    },{passive:true});
    p.wrap.addEventListener('touchmove',function(e){
      if(!dragging||e.touches.length!==1)return;
      var dx=e.touches[0].clientX-lx,dy=e.touches[0].clientY-ly;
      if(Math.abs(dx)>3||Math.abs(dy)>3)didDrag=true;
      tx+=dx;ty+=dy;lx=e.touches[0].clientX;ly=e.touches[0].clientY;clamp();applyAll();
    },{passive:true});
    p.wrap.addEventListener('touchend',function(){dragging=false;});
  });
})();
</script>
</body>
</html>`;
      const blob = new Blob([html], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noreferrer');
    } catch {
      alert('Não foi possível carregar os anexos.');
    }
  };

  const handleOpenFinancialModal = () => {
    resetFinancialForm();
    setShowFinancialModal(true);
  };

  const handleCloseFinancialModal = () => {
    resetFinancialForm();
    setShowFinancialModal(false);
  };

  const handleEditTransaction = async (transaction) => {
    const stripSuffix = (desc) =>
      (desc || '').replace(/(\s*-\s*(Parcela\s+\d+\/\d+|Entrada(\s+\d+)?))+$/g, '').trim();

    const today = getLocalDateInputValue();

    if (transaction.installmentGroup) {
      // Parcela de um grupo: mostra só esta, preserva as demais
      setEditingTransactionId(transaction._id);
      const todayLabel = new Date().toLocaleDateString('pt-BR');
      const originalDate = formatDateForInput(transaction.date); // data original da parcela
      const reparcelNote = `[${todayLabel}] Reparcelamento da Parcela ${transaction.installmentNumber}/${transaction.totalInstallments || '?'} \u2013 R$ ${transaction.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      setFinancialForm({
        description: stripSuffix(transaction.description),
        category: transaction.category || 'Consulta',
        amount: formatAmountForInputValue(transaction.amount),
        date: originalDate,
        paymentStatus: transaction.paymentStatus || 'pendente',
        paymentMethod: transaction.paymentMethod || 'dinheiro',
        hasInstallments: true,
        installments: 1,
        installmentPaymentMethod: transaction.paymentMethod || 'cartao_credito',
        notes: transaction.notes ? `${transaction.notes}\n${reparcelNote}` : reparcelNote,
        quantity: '',
      });
      const scheduleEntry = [{
        installmentNumber: transaction.installmentNumber || 1,
        amount: transaction.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        date: originalDate,
        paymentMethod: transaction.paymentMethod,
        paymentStatus: transaction.paymentStatus
      }];
      overrideSchedule(scheduleEntry);
      // Também define o estado local diretamente para evitar problema de timing do hook
      setInstallmentSchedule(scheduleEntry);
    } else {
      setEditingTransactionId(transaction._id);
      setFinancialForm({
        description: stripSuffix(transaction.description) || transaction.description,
        category: transaction.category || 'Consulta',
        amount: formatAmountForInputValue(transaction.amount),
        date: formatDateForInput(transaction.date),
        paymentStatus: transaction.paymentStatus || 'pendente',
        paymentMethod: transaction.paymentMethod || 'dinheiro',
        hasInstallments: !!transaction.hasInstallments,
        installments: transaction.installments || 1,
        installmentPaymentMethod: transaction.installmentPaymentMethod || 'cartao_credito',
        notes: transaction.notes || '',
        quantity: '',
      });
    }
    setSelectedMedication(null);
    setMedicationQuery('');
    setShowFinancialModal(true);
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Deseja realmente excluir esta movimentação?')) {
      return;
    }
    try {
      await api.delete(`/financial/${transactionId}`);
      fetchFinancialTransactions(currentPatient._id);
    } catch (error) {
      console.error('Erro ao excluir movimentação', error);
      alert('Não foi possível excluir a movimentação.');
    }
  };

  const handleNewConsultation = () => {
    resetConsultationForm();
    setEditingConsultationId(null);
    setEditingConsultation(null);
    // Pre-fill protocol from the most recent consultation
    if (consultations.length > 0) {
      const lastConsultation = [...consultations].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
      if (lastConsultation?.protocol) {
        setConsultationForm((prev) => ({ ...prev, protocol: lastConsultation.protocol }));
      }
    }
    setShowConsultationModal(true);
  };

  const handleConsultationChange = (event) => {
    const { name, value } = event.target;
    setConsultationForm((prev) => ({ ...prev, [name]: value }));
  };

  const closeConsultationModal = () => {
    resetConsultationForm();
    setEditingConsultationId(null);
    setEditingConsultation(null);
    setShowConsultationModal(false);
  };

  const getProductSuggestions = (query) => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return medications.slice(0, 6);
    return medications
      .filter((med) => {
        const name = med.name?.toLowerCase() || '';
        const lab = med.laboratory?.toLowerCase() || '';
        return name.includes(q) || lab.includes(q);
      })
      .slice(0, 6);
  };

  const updateProductRow = (rowId, changes) => {
    setConsultationProducts((prev) =>
      prev.map((row) => (row._rowId === rowId ? { ...row, ...changes } : row))
    );
  };

  const addProductRow = () => {
    setConsultationProducts((prev) => [...prev, emptyProductRow()]);
  };

  const removeProductRow = (rowId) => {
    setConsultationProducts((prev) => prev.filter((row) => row._rowId !== rowId));
  };

  const handleEditConsultation = (consultation) => {
    setEditingConsultationId(consultation._id);
    setEditingConsultation(consultation);
    setConsultationForm({
      date: consultation.date.split('T')[0],
      time: consultation.time || '',
      abdominal:
        consultation.abdominal !== undefined && consultation.abdominal !== null
          ? String(consultation.abdominal)
          : '',
      weight:
        consultation.weight !== undefined && consultation.weight !== null
          ? String(consultation.weight)
          : '',
      notes: consultation.notes || '',
      protocol: consultation.protocol || '',
    });
    if (Array.isArray(consultation.products) && consultation.products.length > 0) {
      const rows = consultation.products.map((p) => {
        const med = medications.find((m) => String(m._id) === String(p.medicationId)) || {
          _id: p.medicationId,
          name: p.productName,
          laboratory: p.laboratory,
          unit: p.unit,
        };
        return { ...emptyProductRow(), query: p.productName || '', selectedMedication: med || null, quantity: p.quantity || '' };
      });
      setConsultationProducts(rows);
    } else {
      const existingMedication =
        medications.find((med) => med._id === (consultation.medicationId || consultation.medication)) ||
        (consultation.medicationId
          ? {
              _id: consultation.medicationId,
              name: consultation.productName,
              laboratory: consultation.laboratory || consultation.productLab,
              unit: consultation.unit,
            }
          : null);
      setConsultationProducts([
        {
          ...emptyProductRow(),
          query: consultation.productName || consultation.product || existingMedication?.name || '',
          selectedMedication: existingMedication,
          quantity: consultation.quantity || '',
        },
      ]);
    }
    setConsultationAttachmentFile(null);
    const existingKey = consultation.attachmentUrl || null;
    setConsultationAttachmentKey(existingKey);
    setConsultationAttachmentPreviewUrl(null);
    if (existingKey) {
      api.get(`/upload/signed-url?key=${encodeURIComponent(existingKey)}`)
        .then(({ data }) => setConsultationAttachmentPreviewUrl(data.url))
        .catch(() => setConsultationAttachmentPreviewUrl(null));
    }
    setShowConsultationModal(true);
  };

  const handleDeleteConsultation = async (consultation) => {
    if (!currentPatient?._id) return;
    const confirmDelete = window.confirm('Deseja realmente excluir esta consulta?');
    if (!confirmDelete) return;
    try {
      const movementsToDelete = (consultation.products || [])
        .map((p) => p.movementId)
        .filter(Boolean);
      if (consultation.movementId && !movementsToDelete.includes(String(consultation.movementId))) {
        movementsToDelete.push(consultation.movementId);
      }
      for (const movId of movementsToDelete) {
        await api.delete(`/inventory/movements/${movId}`);
      }
      if (movementsToDelete.length > 0) await loadMedications();
      await api.delete(`/patients/${currentPatient._id}/consultations/${consultation._id}`);

      // Reverter appointment do dia para "agendado" (não excluir)
      try {
        const rawDate = consultation.date;
        const dateStr = rawDate ? String(rawDate).split('T')[0] : null;
        if (dateStr) {
          const aptsRes = await api.get(`/appointments?date=${dateStr}`);
          const matching = (aptsRes.data || []).find(
            (a) => String(a.patientId?._id || a.patientId) === String(currentPatient._id)
          );
          if (matching && matching.status === 'present') {
            await api.put(`/appointments/${matching._id}`, { status: 'scheduled' });
          }
        }
      } catch (_) {}

      setConsultations((prev) => prev.filter((item) => item._id !== consultation._id));
      onRefreshPatient(currentPatient._id);
    } catch (error) {
      console.error('Erro ao excluir consulta', error);
      alert('Não foi possível excluir a consulta.');
    }
  };

  // Último valor registrado (C.A ou peso) antes da consulta em edição — ou o mais recente, se for nova.
  // Usado para manter a evolução do paciente quando o campo fica em branco.
  const getPreviousMeasure = (field) => {
    const refDate = editingConsultation ? new Date(editingConsultation.date) : null;
    const previous = [...consultations]
      .filter((c) => c._id !== editingConsultationId)
      .filter((c) => !refDate || new Date(c.date) <= refDate)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .find((c) => Number(c[field]) > 0);
    return previous ? Number(previous[field]) : null;
  };

  const handleConsultationSubmit = async (event) => {
    event.preventDefault();
    // Validate rows that have a product selected
    for (const row of consultationProducts) {
      if (row.selectedMedication?._id) {
        const qty = Number(row.quantity);
        if (!qty || qty <= 0 || Number.isNaN(qty)) {
          alert(`Informe uma quantidade válida para "${row.selectedMedication.name}".`);
          return;
        }
        const available = Number(row.selectedMedication.totalQuantity ?? 0);
        if (!editingConsultationId && qty > available) {
          alert(`Quantidade de "${row.selectedMedication.name}" maior que o disponível em estoque (${available}).`);
          return;
        }
      }
    }
    const isEditing = Boolean(editingConsultationId);
    const createdMovementIds = [];
    try {
      const productsPayload = [];
      for (const row of consultationProducts) {
        if (!row.selectedMedication?._id) continue;
        const qty = Number(row.quantity);
        if (!qty || qty <= 0) continue;
        let movementId = null;
        if (!isEditing) {
          const movementPayload = {
            medicationId: row.selectedMedication._id,
            type: 'Saída',
            quantity: Math.abs(qty),
            patient: currentPatient?.name || '',
            note: `Consulta registrada • ${row.selectedMedication.name}`,
            date: consultationForm.date,
          };
          const { data: movementData } = await api.post('/inventory/movements', movementPayload);
          movementId = movementData?.movement?._id || movementData?.movement?.id || null;
          if (movementId) createdMovementIds.push(movementId);
          const updatedMedication = movementData?.medication;
          if (updatedMedication) {
            setMedications((prev) =>
              prev.map((med) => (med._id === updatedMedication._id ? updatedMedication : med))
            );
          }
        } else {
          const existingProduct = (editingConsultation?.products || []).find(
            (p) => String(p.medicationId) === String(row.selectedMedication._id)
          );
          movementId = existingProduct?.movementId || null;
        }
        productsPayload.push({
          medicationId: row.selectedMedication._id,
          productName: row.selectedMedication.name,
          laboratory: row.selectedMedication.laboratory || '',
          unit: row.selectedMedication.unit || '',
          quantity: String(qty),
          movementId,
        });
      }
      let finalKey = consultationAttachmentKey;
      if (consultationAttachmentFile) {
        const formData = new FormData();
        formData.append('file', consultationAttachmentFile);
        const { data: uploadData } = await api.upload('/upload', formData);
        finalKey = uploadData.key;
      }
      const firstProduct = productsPayload[0];
      const payload = {
        date: consultationForm.date,
        time: consultationForm.time || '',
        medicationId: firstProduct?.medicationId || null,
        productName: firstProduct?.productName || '',
        laboratory: firstProduct?.laboratory || '',
        quantity: firstProduct?.quantity || '',
        unit: firstProduct?.unit || '',
        abdominal: String(consultationForm.abdominal ?? '').trim()
          ? parseLocalizedNumber(consultationForm.abdominal)
          : getPreviousMeasure('abdominal'),
        weight: String(consultationForm.weight ?? '').trim()
          ? parseLocalizedNumber(consultationForm.weight)
          : getPreviousMeasure('weight'),
        movementId: isEditing ? editingConsultation?.movementId : (firstProduct?.movementId || null),
        attachmentUrl: finalKey || null,
        notes: consultationForm.notes || '',
        protocol: consultationForm.protocol || '',
        products: productsPayload,
      };
      if (isEditing) {
        const { data: updatedConsultation } = await api.put(
          `/patients/${currentPatient?._id}/consultations/${editingConsultationId}`,
          payload
        );
        setConsultations((prev) => prev.map((c) => (c._id === editingConsultationId ? { ...updatedConsultation, productLab: updatedConsultation.laboratory || updatedConsultation.productLab } : c)));
      } else {
        const { data: savedConsultation } = await api.post(
          `/patients/${currentPatient?._id}/consultations`,
          payload
        );
        setConsultations((prev) => [...prev, { ...savedConsultation, productLab: savedConsultation.laboratory || savedConsultation.productLab }]);
        try {
          await createPresenceAppointmentForConsultation(payload.date, payload.time);
        } catch (autoAppointmentError) {
          console.error('Erro ao criar agendamento automático', autoAppointmentError);
          alert('Consulta salva, mas não foi possível registrar o comparecimento na agenda.');
        }
      }
      closeConsultationModal();
      onRefreshPatient(currentPatient?._id);
    } catch (error) {
      for (const movId of createdMovementIds) {
        try {
          await api.delete(`/inventory/movements/${movId}`);
        } catch (cleanupError) {
          console.error('Erro ao remover movimentação após falha na consulta', cleanupError);
        }
      }
      console.error('Erro ao salvar consulta do paciente', error);
      alert('Não foi possível salvar a consulta.');
    }
  };

  const consultationsWithLoss = useMemo(() => {
    const byDate = [...consultations].sort((a, b) => new Date(a.date) - new Date(b.date));
    const withLoss = byDate.map((item, index) => {
      const currentWeight = Number(item.weight) || 0;
      // Busca o peso da consulta anterior mais recente que tenha peso registrado
      let previousWeight = null;
      for (let i = index - 1; i >= 0; i--) {
        const w = Number(byDate[i].weight);
        if (w > 0) { previousWeight = w; break; }
      }
      let percent = null;
      let weightLoss = null;
      if (currentWeight > 0 && previousWeight && previousWeight > 0) {
        const diff = previousWeight - currentWeight;
        percent = (diff / previousWeight) * 100;
        weightLoss = diff;
      }
      // Busca o C.A da consulta anterior mais recente que tenha C.A registrado
      const currentCA = Number(item.abdominal) || 0;
      let previousCA = null;
      for (let i = index - 1; i >= 0; i--) {
        const ca = Number(byDate[i].abdominal);
        if (ca > 0) { previousCA = ca; break; }
      }
      let caLoss = null;
      let caPercent = null;
      if (currentCA > 0 && previousCA && previousCA > 0) {
        caLoss = previousCA - currentCA;
        caPercent = (caLoss / previousCA) * 100;
      }
      return { ...item, percent, weightLoss, caLoss, caPercent };
    });
    return [...withLoss].sort((a, b) => {
      const { key, dir } = consultSort;
      let va = a[key], vb = b[key];
      if (key === 'date') { va = new Date(va || 0); vb = new Date(vb || 0); }
      else if (key === 'weight' || key === 'abdominal' || key === 'quantity' || key === 'weightLoss' || key === 'percent' || key === 'caLoss' || key === 'caPercent') {
        va = Number(va) || 0; vb = Number(vb) || 0;
      } else {
        va = (va || '').toString().toLowerCase(); vb = (vb || '').toString().toLowerCase();
      }
      if (va < vb) return dir === 'asc' ? -1 : 1;
      if (va > vb) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [consultations, consultSort]);
  // IDs das consultas que são a 2ª após uma com anexo (bioimpedância) → mostra aviso na linha
  const bioimpedanceReminderIds = useMemo(() => {
    const sorted = [...consultations].sort((a, b) => new Date(a.date) - new Date(b.date));
    const ids = new Set();
    sorted.forEach((c, i) => {
      if (c.attachmentUrl && i + 2 < sorted.length) {
        ids.add(sorted[i + 2]._id);
      }
    });
    return ids;
  }, [consultations]);

  // True se a nova consulta a ser criada é a 3ª após uma com anexo → mostra banner no modal
  const showBioimpedanceAlert = useMemo(() => {
    const sorted = [...consultations].sort((a, b) => new Date(a.date) - new Date(b.date));
    for (let i = sorted.length - 1; i >= 0; i--) {
      if (sorted[i].attachmentUrl) {
        return sorted.length - 1 - i === 2;
      }
    }
    return false;
  }, [consultations]);

  const consultationsWithValidWeight = useMemo(() => {
    return consultations
      .filter((c) => c.weight !== undefined && c.weight !== null && c.weight !== '')
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [consultations]);
  const firstWeightEntry = consultationsWithValidWeight[0];
  const lastWeightEntry = consultationsWithValidWeight[consultationsWithValidWeight.length - 1];
  const lastWeightFromConsultations = lastWeightEntry ? Number(lastWeightEntry.weight) : null;
  const lastConsultWeight =
    lastWeightFromConsultations ||
    Number(currentPatient?.weight) ||
    0;
  const initialWeight = firstWeightEntry ? Number(firstWeightEntry.weight) || null : null;
  const weightLossTotal =
    initialWeight && lastConsultWeight ? initialWeight - lastConsultWeight : null;
  const totalWeightLossPercent = (() => {
    if (!initialWeight || !lastConsultWeight || initialWeight === 0) return null;
    return ((initialWeight - lastConsultWeight) / initialWeight) * 100;
  })();
  const weeklyWeightLoss = useMemo(() => {
    // Precisa de 2+ consultas COM peso; checar só consultations.length quebrava a tela
    const sorted = consultations.filter(c => c.weight).sort((a, b) => new Date(a.date) - new Date(b.date));
    if (sorted.length >= 2) {
      const last = parseFloat(sorted[sorted.length - 1].weight);
      const previous = parseFloat(sorted[sorted.length - 2].weight);
      return previous - last;
    }
    return null;
  }, [consultations]);
  const sortedByDate = useMemo(() => [...consultations].sort((a, b) => new Date(a.date) - new Date(b.date)), [consultations]);
  const firstAbdominal = (() => { for (const c of sortedByDate) { const v = Number(c.abdominal); if (v > 0) return v; } return null; })();
  const lastAbdominal = (() => { for (const c of [...sortedByDate].reverse()) { const v = Number(c.abdominal); if (v > 0) return v; } return null; })();
  const abdominalLoss =
    firstAbdominal && lastAbdominal && firstAbdominal !== lastAbdominal ? firstAbdominal - lastAbdominal : null;
  const weightChartData = useMemo(() => {
    return consultationsWithValidWeight.map((c) => ({
      date: formatDateLabel(c.date),
      weight: Number(c.weight),
    }));
  }, [consultationsWithValidWeight]);

  const { weightDomain, weightTicks } = useMemo(() => {
    if (!weightChartData.length) {
      return {
        weightDomain: [0, 1],
        weightTicks: [0, 1],
      };
    }
    const weights = weightChartData.map((entry) => entry.weight);
    const min = Math.min(...weights);
    const max = Math.max(...weights);
    const buffer = 1;
    let step = 1;
    let range = max - min + buffer * 2;
    if (range > 20) step = 5;
    else if (range > 10) step = 2;
    // Ajusta início e fim para múltiplos do step
    let start = Math.floor((min - buffer) / step) * step;
    let end = Math.ceil((max + buffer) / step) * step;
    if (start === end) {
      end = start + step;
    }
    const ticks = [];
    for (let val = start; val <= end; val += step) {
      ticks.push(Number(val.toFixed(1)));
    }
    return {
      weightDomain: [start, end],
      weightTicks: ticks,
    };
  }, [weightChartData]);

  const metricCards = [
    {
      key: 'initial-weight',
      title: 'Peso Inicial',
      icon: MdScale,
      gradient: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
      value: formatWeightLabel(initialWeight !== null ? displayedInitialWeight : null),
    },
    {
      key: 'current-weight',
      title: 'Peso Atual',
      icon: MdTrendingUp,
      gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      value: formatWeightLabel(lastConsultWeight ? displayedLastConsultWeight : null),
    },
    {
      key: 'lost-weight',
      title: 'Peso Perdido',
      icon: MdTrendingDown,
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      value: formatWeightLabel(weightLossTotal !== null ? displayedWeightLossTotal : null),
    },
    {
      key: 'weekly-loss',
      title: 'Perda Última Consulta',
      icon: MdTrendingDown,
      gradient: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
      value: formatWeightLabel(weeklyWeightLoss !== null ? displayedWeeklyWeightLoss : null),
    },
    {
      key: 'percent-loss',
      title: '% Perdido',
      icon: MdPercent,
      gradient: 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)',
      value: totalWeightLossPercent !== null ? formatPercentage(displayedTotalWeightLossPercent) : '—',
    },
    {
      key: 'abdominal-loss',
      title: 'C.A Perdido',
      icon: MdTrendingDown,
      gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
      value: abdominalLoss !== null ? `${displayedAbdominalLoss.toFixed(1)} cm` : '—',
    },
  ];

  // Animation for counters
  useEffect(() => {
    if (activeTab !== 'dashboard') return;

    // Reset displayed values to 0 or initial
    setDisplayedInitialWeight(0);
    setDisplayedLastConsultWeight(initialWeight || 0);
    setDisplayedWeightLossTotal(0);
    setDisplayedTotalWeightLossPercent(0);
    setDisplayedWeeklyWeightLoss(0);
    setDisplayedAbdominalLoss(0);

    const animateValue = (end, setValue, startValue = 0, duration = 2000) => {
      if (end === null || end === undefined) return;
      const endValue = end;
      const steps = 50; // 50 steps over 5 seconds for lighter animation
      const increment = (endValue - startValue) / steps;
      let current = startValue;
      const timer = setInterval(() => {
        current += increment;
        if ((increment > 0 && current >= endValue) || (increment < 0 && current <= endValue)) {
          setValue(endValue);
          clearInterval(timer);
        } else {
          setValue(current);
        }
      }, duration / steps);
      return () => clearInterval(timer);
    };

    const cleanup1 = animateValue(initialWeight, setDisplayedInitialWeight);
    const cleanup2 = animateValue(lastConsultWeight, setDisplayedLastConsultWeight, initialWeight || 0);
    const cleanup3 = animateValue(weightLossTotal, setDisplayedWeightLossTotal);
    const cleanup4 = animateValue(totalWeightLossPercent, setDisplayedTotalWeightLossPercent);
    const cleanup5 = animateValue(weeklyWeightLoss, setDisplayedWeeklyWeightLoss);
    const cleanup6 = animateValue(abdominalLoss, setDisplayedAbdominalLoss);

    return () => {
      cleanup1?.();
      cleanup2?.();
      cleanup3?.();
      cleanup4?.();
      cleanup5?.();
      cleanup6?.();
    };
  }, [activeTab, initialWeight, lastConsultWeight, weightLossTotal, totalWeightLossPercent, weeklyWeightLoss, abdominalLoss]);

  const handleSubmitFinancial = async (event) => {
    event.preventDefault();
    if (!currentPatient?._id) return;
    let createdMovementId = null;
    let updatedMedicationSnapshot = null;
    const isEditing = Boolean(editingTransactionId);
    try {
      const amountValue = parseAmountToNumber(financialForm.amount);
      if (amountValue <= 0) {
        alert('Informe um valor válido.');
        return;
      }

      if (financialForm.hasInstallments) {
        if (installmentSchedule.length === 0) {
          alert('Defina as parcelas antes de salvar.');
          return;
        }
        const totalInstallmentsValue = installmentSchedule.reduce((sum, inst) => {
          if (!inst.amount) return sum;
          return sum + parseAmountToNumber(inst.amount);
        }, 0);
        const difference = amountValue - totalInstallmentsValue;
        if (Math.abs(difference) > 0.01) {
          alert('A soma das parcelas deve ser igual ao valor total.');
          return;
        }
      }

      const normalizedDate = normalizeDateToISO(financialForm.date) || new Date().toISOString();

      if (financialForm.category === 'Produto' && !isEditing) {
        if (!selectedMedication?._id) {
          alert('Selecione um medicamento do estoque.');
          return;
        }
        const quantityNumber = Number(financialForm.quantity);
        if (!quantityNumber || Number.isNaN(quantityNumber) || quantityNumber <= 0) {
          alert('Informe a quantidade aplicada ao paciente.');
          return;
        }
        const available = Number(selectedMedication.totalQuantity ?? 0);
        if (quantityNumber > available) {
          alert('Quantidade informada maior que o estoque disponível para este medicamento.');
          return;
        }
        try {
          const movementPayload = {
            medicationId: selectedMedication._id,
            type: 'Saída',
            quantity: Math.abs(quantityNumber),
            patient: currentPatient?.name || '',
            note: `Movimentação financeira • ${financialForm.description}`,
            date: normalizedDate,
          };
          const { data: movementResponse } = await api.post('/inventory/movements', movementPayload);
          createdMovementId = movementResponse?.movement?._id || movementResponse?.movement?.id || null;
          updatedMedicationSnapshot = movementResponse?.medication || null;
        } catch (movementError) {
          console.error('Erro ao registrar saída no estoque', movementError);
          alert('Não foi possível registrar o consumo do estoque. Tente novamente.');
          return;
        }
      }

      const medicationNote =
        financialForm.category === 'Produto' && selectedMedication
          ? [
              `Medicamento: ${selectedMedication.name}${
                selectedMedication.laboratory ? ` (${selectedMedication.laboratory})` : ''
              }`,
              financialForm.quantity ? `Quantidade: ${financialForm.quantity} ${selectedMedication.unit || 'unid.'}` : '',
            ]
              .filter(Boolean)
              .join(' • ')
          : '';

      const payload = {
        type: 'receita',
        category: financialForm.category,
        description: financialForm.description?.trim() || selectedMedication?.name || '',
        amount: amountValue,
        date: normalizedDate,
        paymentStatus: financialForm.paymentStatus,
        paymentMethod: financialForm.paymentMethod,
        patientId: currentPatient._id,
        notes: [financialForm.notes, medicationNote].filter(Boolean).join('\n'),
        hasInstallments: financialForm.hasInstallments,
        installments: financialForm.hasInstallments ? financialForm.installments : 1,
        installmentPaymentMethod: financialForm.installmentPaymentMethod,
      };

      if (!financialForm.hasInstallments) {
        payload.installments = 1;
        delete payload.installmentPaymentMethod;
        delete payload.customInstallments;
      } else if (installmentSchedule.length > 0) {
        payload.customInstallments = installmentSchedule.map((inst, index) => ({
          amount: parseAmountToNumber(inst.amount),
          paymentMethod: inst.paymentMethod,
          date: inst.date ? normalizeDateToISO(inst.date) : undefined,
          installmentNumber: inst.installmentNumber || index + 1,
          paymentStatus: financialForm.paymentStatus || inst.paymentStatus || 'pendente',
        }));
      }

      if (createdMovementId) {
        payload.relatedTo = createdMovementId;
      }

      if (editingTransactionId) {
        await api.put(`/financial/${editingTransactionId}`, payload);
      } else {
        await api.post('/financial', payload);
      }
      if (updatedMedicationSnapshot) {
        setMedications((prev) =>
          prev.map((med) => (med._id === updatedMedicationSnapshot._id ? updatedMedicationSnapshot : med))
        );
      }
      handleCloseFinancialModal();
      fetchFinancialTransactions(currentPatient._id);
    } catch (error) {
      if (createdMovementId) {
        try {
          await api.delete(`/inventory/movements/${createdMovementId}`);
        } catch (cleanupError) {
          console.error('Erro ao desfazer movimentação de estoque', cleanupError);
        }
      }
      console.error('Erro ao criar movimentação financeira', error);
      alert('Não foi possível criar a movimentação financeira.');
    }
  };

  if (patientIdFromQuery && !resolvedPatient) {
    return (
      <div className="patients-card">
        <div className="patients-content">
          <p className="helper-text">Carregando paciente...</p>
        </div>
      </div>
    );
  }

  if (!resolvedPatient) {
    return (
      <div className="patients-card">
        <div className="patients-content">
          <p className="helper-text">Nenhum paciente selecionado.</p>
          <button className="mac-button" onClick={() => navigate('/pacientes')}>
            Voltar para pacientes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="patients-card">
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `
      }} />
      <div className="patients-content">
        <style dangerouslySetInnerHTML={{ __html: `
          @keyframes badge-slide-in {
            from { opacity: 0; transform: translateX(-10px); }
            to   { opacity: 1; transform: translateX(0); }
          }
          @keyframes badge-pulse {
            0%, 100% { opacity: 1; }
            50%       { opacity: 0.4; }
          }
        ` }} />
        <div className="patients-header">
          <div className="patients-title" style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <h1 style={{
                fontWeight: 700, fontSize: 38, lineHeight: 1.18,
                letterSpacing: '-0.035em', margin: 0, color: '#1C1C1E',
              }}>
                {toTitleCase(currentPatient?.name)}
              </h1>
              <span style={{ marginTop: 6, fontSize: 14, color: '#6E6E73', letterSpacing: '-0.005em' }}>
                {getDetailedAge(currentPatient?.birthDate)}
              </span>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                background: '#FBE0E5', color: '#9F4A60',
                padding: '4px 10px', borderRadius: 999,
                fontSize: 11, letterSpacing: '-0.005em', fontWeight: 600,
                whiteSpace: 'nowrap', marginTop: 8,
                animation: 'badge-slide-in 0.5s ease 0.2s both',
              }}>
                <span style={{
                  width: 5, height: 5, borderRadius: 999, background: '#C97A8F',
                  animation: 'badge-pulse 2s ease-in-out 0.7s infinite',
                }} />
                Em acompanhamento
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {consultations.filter(c => c.attachmentUrl).length > 0 && (
              <button
                type="button"
                onClick={handleOpenBiocompare}
                style={{
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em',
                  padding: '9px 14px', borderRadius: 10, border: '1px solid #E5E5EA',
                  background: '#FFFFFF', color: '#1C1C1E', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                  <path d="M7 16V4M4 7l3-3 3 3M17 8v12M14 17l3 3 3-3" />
                </svg>
                Comparar Bioimpedâncias
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/pacientes')}
              style={{
                fontFamily: 'inherit', fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em',
                padding: '9px 18px', borderRadius: 10, border: '1px solid #1C1C1E',
                background: '#1C1C1E', color: '#FFFFFF', cursor: 'pointer',
              }}
            >
              Voltar
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 28, marginTop: 28, borderBottom: '1px solid #E5E5EA', fontSize: 14 }}>
          {[
            { key: 'dashboard', label: 'Dashboard' },
            { key: 'patientData', label: 'Dados do Paciente' },
            { key: 'applications', label: 'Consultas' },
            { key: 'financial', label: 'Movimentação Financeira' },
          ].map((tab) => (
            <div
              key={tab.key}
              role="button"
              tabIndex={0}
              onClick={() => setActiveTab(tab.key)}
              onKeyDown={e => e.key === 'Enter' && setActiveTab(tab.key)}
              style={{
                paddingBottom: 12,
                marginBottom: -1,
                borderBottom: activeTab === tab.key ? '2px solid #1C1C1E' : '2px solid transparent',
                color: activeTab === tab.key ? '#1C1C1E' : '#6E6E73',
                fontWeight: activeTab === tab.key ? 600 : 500,
                letterSpacing: '-0.005em',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </div>
          ))}
        </div>
        {activeTab === 'dashboard' && (() => {
          const kpiTones = {
            inicial: { bg: ['#FBEEDF', '#F4DCC4'], accent: '#A66730' },
            atual:   { bg: ['#FBE0E5', '#F4C4CE'], accent: '#9F4A60' },
            perdido: { bg: ['#EFE3F0', '#DECCDE'], accent: '#7A4A86' },
            ultima:  { bg: ['#E8E3DA', '#D7CFC1'], accent: '#65564A' },
            pct:     { bg: ['#F4DBDE', '#E5BFC3'], accent: '#A8556A' },
            ca:      { bg: ['#E5DCEC', '#CFC0DC'], accent: '#634A82' },
          };

          const KpiIcon = ({ kind, color }) => {
            const p = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };
            if (kind === 'scale')   return <svg {...p}><path d="M4 21h16M6 21l2-10h8l2 10M12 4v3M9 4h6M9 11a3 3 0 006 0" /></svg>;
            if (kind === 'spark')   return <svg {...p}><path d="M3 17l5-6 4 3 4-5 5 4" /></svg>;
            if (kind === 'down')    return <svg {...p}><path d="M3 7l5 6 4-3 4 5 5-4M16 17h5v-5" /></svg>;
            if (kind === 'delta')   return <svg {...p}><path d="M12 4l8 14H4z" /></svg>;
            if (kind === 'percent') return <svg {...p}><circle cx="7" cy="7" r="2.5"/><circle cx="17" cy="17" r="2.5"/><path d="M19 5L5 19"/></svg>;
            if (kind === 'ruler')   return <svg {...p}><path d="M3 8l5-5 13 13-5 5zM8 3l2 2M12 7l2 2M16 11l2 2M20 15l1 1"/></svg>;
            return null;
          };

          const kpiCards = [
            { key: 'inicial', icon: 'scale', label: 'Peso Inicial',          value: formatWeightValue(initialWeight !== null ? displayedInitialWeight : null),              unit: 'kg',  delta: false },
            { key: 'atual',   icon: 'spark', label: 'Peso Atual',            value: formatWeightValue(lastConsultWeight ? displayedLastConsultWeight : null),                unit: 'kg',  delta: false },
            { key: 'perdido', icon: 'down',  label: 'Peso Perdido',          value: formatWeightValue(weightLossTotal !== null ? displayedWeightLossTotal : null),           unit: 'kg',  delta: true },
            { key: 'ultima',  icon: 'delta', label: 'Perda Última Consulta', value: formatWeightValue(weeklyWeightLoss !== null ? displayedWeeklyWeightLoss : null),          unit: 'kg',  delta: true },
            { key: 'pct',     icon: 'percent',label: '% Perdido',            value: totalWeightLossPercent !== null ? displayedTotalWeightLossPercent.toFixed(1) : null,     unit: '%',   delta: true },
            { key: 'ca',      icon: 'ruler', label: 'C.A Perdido',           value: abdominalLoss !== null ? displayedAbdominalLoss.toFixed(1) : null,                       unit: 'cm',  delta: true },
          ];

          const makeSmoothPath = (points) => {
            if (points.length < 2) return '';
            let d = `M ${points[0][0]},${points[0][1]}`;
            for (let i = 0; i < points.length - 1; i++) {
              const p0 = points[i - 1] || points[i];
              const p1 = points[i];
              const p2 = points[i + 1];
              const p3 = points[i + 2] || p2;
              const t = 0.18;
              const c1x = p1[0] + (p2[0] - p0[0]) * t;
              const c1y = p1[1] + (p2[1] - p0[1]) * t;
              const c2x = p2[0] - (p3[0] - p1[0]) * t;
              const c2y = p2[1] - (p3[1] - p1[1]) * t;
              d += ` C ${c1x},${c1y} ${c2x},${c2y} ${p2[0]},${p2[1]}`;
            }
            return d;
          };

          const svgW = 1080, svgH = 385;
          const padL = 56, padR = 24, padT = 20, padB = 40;
          const innerW = svgW - padL - padR;
          const innerH = svgH - padT - padB;
          const yMin = weightDomain[0], yMax = weightDomain[1];
          const chartPoints = weightChartData.length > 1
            ? weightChartData.map((d, i) => [
                padL + (i / (weightChartData.length - 1)) * innerW,
                padT + (1 - (d.weight - yMin) / (yMax - yMin)) * innerH,
              ])
            : [];
          const linePath = makeSmoothPath(chartPoints);
          const areaPath = chartPoints.length
            ? linePath + ` L ${chartPoints[chartPoints.length - 1][0]},${padT + innerH} L ${chartPoints[0][0]},${padT + innerH} Z`
            : '';
          const yTickData = weightTicks.map(v => ({
            y: padT + (1 - (v - yMin) / (yMax - yMin)) * innerH,
            label: v,
          }));
          const xStep = Math.max(1, Math.ceil(weightChartData.length / 7));
          const xTickData = (() => {
            const candidates = weightChartData
              .map((d, i) => ({ d, i }))
              .filter(({ i }) => i === 0 || i === weightChartData.length - 1 || i % xStep === 0)
              .map(({ d, i }) => ({ x: chartPoints[i]?.[0], label: d.date, isLast: i === weightChartData.length - 1 }))
              .filter(t => t.x !== undefined);
            const minDist = 90;
            const result = [];
            for (const tick of candidates) {
              const prev = result[result.length - 1];
              if (!prev || tick.x - prev.x >= minDist) {
                result.push(tick);
              } else if (tick.isLast) {
                result[result.length - 1] = tick;
              }
            }
            return result;
          })();

          return (
            <div style={{ marginTop: 40 }}>
              {/* Section header */}
              <div style={{ marginBottom: 18 }}>
                <h2 style={{ fontWeight: 700, fontSize: 26, margin: 0, letterSpacing: '-0.025em', color: '#1C1C1E' }}>
                  Dashboard de progresso
                </h2>
              </div>

              {/* KPI grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12 }}>
                {kpiCards.map(({ key, icon, label, value, unit, delta }) => {
                  const t = kpiTones[key];
                  return (
                    <div key={key} style={{
                      background: `linear-gradient(160deg, ${t.bg[0]} 0%, ${t.bg[1]} 100%)`,
                      borderRadius: 20, padding: '14px 18px', minHeight: 134,
                      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55), 0 1px 2px rgba(28,28,30,0.04), 0 8px 24px -16px rgba(28,28,30,0.18)',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{
                          width: 30, height: 30, borderRadius: 10,
                          background: 'rgba(255,255,255,0.6)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7), 0 1px 2px rgba(0,0,0,0.04)',
                        }}>
                          <KpiIcon kind={icon} color={t.accent} />
                        </div>
                        {delta && value !== null && (
                          <div style={{
                            fontSize: 10, fontWeight: 600, letterSpacing: '-0.005em', color: t.accent,
                            background: 'rgba(255,255,255,0.55)', padding: '3px 7px', borderRadius: 999,
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                          }}>
                            <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                              <path d="M5 1v8M5 9l3-3M5 9l-3-3" stroke={t.accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            redução
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{
                          fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
                          color: 'rgba(28,28,30,0.6)', marginBottom: 6,
                        }}>
                          {label}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                          <span style={{
                            fontWeight: 700, fontSize: 32, lineHeight: 1,
                            letterSpacing: '-0.04em', color: value !== null ? t.accent : 'rgba(28,28,30,0.3)',
                            fontVariantNumeric: 'tabular-nums',
                          }}>
                            {value !== null ? value : '—'}
                          </span>
                          {value !== null && (
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(28,28,30,0.55)', letterSpacing: '-0.01em' }}>
                              {unit}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Chart card */}
              <div style={{
                marginTop: 14, background: '#FFFFFF', border: '1px solid #E5E5EA', borderRadius: 20,
                padding: '24px 28px 18px',
                boxShadow: '0 1px 2px rgba(28,28,30,0.04), 0 12px 32px -20px rgba(28,28,30,0.12)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                  <div>
                    <h3 style={{ fontWeight: 700, fontSize: 18, margin: 0, letterSpacing: '-0.022em', color: '#1C1C1E' }}>
                      Evolução do peso
                    </h3>
                    <div style={{ marginTop: 4, fontSize: 12, color: '#6E6E73', letterSpacing: '-0.005em' }}>
                      quilogramas
                    </div>
                  </div>
                </div>
                {weightChartData.length > 1 ? (
                  <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" style={{ display: 'block' }}>
                    <defs>
                      <linearGradient id="sc-area" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#C97A8F" stopOpacity="0.28" />
                        <stop offset="100%" stopColor="#C97A8F" stopOpacity="0" />
                      </linearGradient>
                      <linearGradient id="sc-line" x1="0" x2="1" y1="0" y2="0">
                        <stop offset="0%" stopColor="#B86880" />
                        <stop offset="100%" stopColor="#9F4A60" />
                      </linearGradient>
                    </defs>
                    {yTickData.map((t, i) => (
                      <g key={i}>
                        <line x1={padL} y1={t.y} x2={svgW - padR} y2={t.y} stroke="#F2F2F7" strokeWidth="1" />
                        <text x={padL - 10} y={t.y + 3} fontSize="10" fill="#8E8E93" textAnchor="end" fontWeight="500">{t.label}</text>
                      </g>
                    ))}
                    <style>{`
                      @keyframes sc-draw-loop {
                        0%   { stroke-dashoffset: 1; opacity: 0; }
                        4%   { stroke-dashoffset: 1; opacity: 1; }
                        20%  { stroke-dashoffset: 0; opacity: 1; }
                        72%  { stroke-dashoffset: 0; opacity: 1; }
                        84%  { stroke-dashoffset: 0; opacity: 0; }
                        100% { stroke-dashoffset: 1; opacity: 0; }
                      }
                      @keyframes sc-area-loop {
                        0%   { opacity: 0; }
                        20%  { opacity: 0; }
                        32%  { opacity: 1; }
                        72%  { opacity: 1; }
                        84%  { opacity: 0; }
                        100% { opacity: 0; }
                      }
                      @keyframes sc-dot-loop {
                        0%   { opacity: 0; transform: scale(0); }
                        17%  { opacity: 0; transform: scale(0); }
                        24%  { opacity: 1; transform: scale(1.35); }
                        28%  { opacity: 1; transform: scale(1); }
                        72%  { opacity: 1; transform: scale(1); }
                        84%  { opacity: 0; transform: scale(0); }
                        100% { opacity: 0; transform: scale(0); }
                      }
                      .sc-line { stroke-dasharray: 1; animation: sc-draw-loop 9s cubic-bezier(0.4,0,0.2,1) infinite; }
                      .sc-area { animation: sc-area-loop 9s ease infinite; }
                      .sc-dot  { transform-box: fill-box; transform-origin: center; animation: sc-dot-loop 9s ease infinite; }
                      .sc-point .sc-tooltip { opacity: 0; pointer-events: none; transition: opacity 0.15s; }
                      .sc-point:hover .sc-tooltip { opacity: 1; }
                      .sc-point { cursor: crosshair; }
                    `}</style>
                    <path d={areaPath} fill="url(#sc-area)" className="sc-area" />
                    <path d={linePath} fill="none" stroke="url(#sc-line)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" pathLength="1" className="sc-line" />
                    {chartPoints.map((p, i) => {
                      const d = weightChartData[i];
                      const pillW = 78, pillH = 24;
                      const pillX = p[0] - pillW / 2;
                      const pillY = p[1] - pillH - 12;
                      const stagger = i * (0.35 / Math.max(chartPoints.length - 1, 1));
                      return (
                        <g key={i} className="sc-point">
                          <circle cx={p[0]} cy={p[1]} r="12" fill="transparent" />
                          <circle cx={p[0]} cy={p[1]} r="2.8" fill="#FFFFFF" stroke="#9F4A60" strokeWidth="1.4" className="sc-dot" style={{ animationDelay: `${stagger}s` }} />
                          <g className="sc-tooltip" transform={`translate(${pillX}, ${pillY < padT ? p[1] + 12 : pillY})`}>
                            <rect rx="6" width={pillW} height={pillH} fill="#1C1C1E" />
                            <text x={pillW / 2} y="16" fontSize="11" fontWeight="600" fill="#FFFFFF" textAnchor="middle" letterSpacing="-0.01em">
                              {formatWeightValue(d.weight)} kg
                            </text>
                          </g>
                        </g>
                      );
                    })}
                    {xTickData.map((t, i) => (
                      <text
                        key={i} x={t.x} y={svgH - 10} fontSize="10" fill="#8E8E93" fontWeight="500"
                        textAnchor={i === 0 ? 'start' : i === xTickData.length - 1 ? 'end' : 'middle'}
                      >{t.label}</text>
                    ))}
                  </svg>
                ) : (
                  <p style={{ textAlign: 'center', color: '#8E8E93', padding: '20px' }}>
                    Nenhum dado de peso disponível.
                  </p>
                )}
              </div>
            </div>
          );
        })()}
        {activeTab === 'patientData' && (
          <div className="patient-details" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '24px' }}>
          <section className="detail-card">
            <div className="detail-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
              <h2 style={{ margin: 0 }}>Dados principais</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {editingMain ? (
                  <>
                    <button type="button" className="ghost-button" onClick={() => handleCancel('main')}>
                      Cancelar
                    </button>
                    <button type="button" className="mac-button" onClick={() => handleSave('main')}>
                      Salvar
                    </button>
                  </>
                ) : (
                  <button type="button" className="ghost-button" onClick={() => setEditingMain(true)}>
                    Editar
                  </button>
                )}
              </div>
            </div>
            <ul style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <li style={listItemStyle}>
                <strong>Nome:</strong>
                {editingMain ? (
                  <input
                    className="mac-input"
                    name="name"
                    value={draftPatient?.name || ''}
                    onChange={handleFieldChange}
                    placeholder="Nome completo"
                    style={fullWidthInputStyle}
                  />
                ) : (
                  toTitleCase(currentPatient?.name) || '—'
                )}
              </li>
              <li style={listItemStyle}>
                <strong>CPF:</strong>
                {editingMain ? (
                  <input
                    className="mac-input"
                    name="cpf"
                    value={draftPatient?.cpf || ''}
                    onChange={handleFieldChange}
                    placeholder="CPF"
                    style={fullWidthInputStyle}
                  />
                ) : (
                  currentPatient?.cpf || '—'
                )}
              </li>
              <li style={listItemStyle}>
                <strong>Endereço:</strong>
                {editingMain ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        className="mac-input"
                        name="address"
                        value={draftPatient?.address || ''}
                        onChange={handleFieldChange}
                        placeholder="Logradouro"
                        style={{ flex: '1' }}
                      />
                      <input
                        className="mac-input"
                        name="number"
                        value={draftPatient?.number || ''}
                        onChange={handleFieldChange}
                        placeholder="Número"
                        style={{ width: '100px' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        className="mac-input"
                        name="complement"
                        value={draftPatient?.complement || ''}
                        onChange={handleFieldChange}
                        placeholder="Complemento"
                        style={{ flex: '1' }}
                      />
                      <input
                        className="mac-input"
                        name="district"
                        value={draftPatient?.district || ''}
                        onChange={handleFieldChange}
                        placeholder="Bairro"
                        style={{ flex: '1' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        className="mac-input"
                        name="city"
                        value={draftPatient?.city || ''}
                        onChange={handleFieldChange}
                        placeholder="Cidade"
                        style={{ flex: '1' }}
                      />
                      <input
                        className="mac-input"
                        name="state"
                        value={draftPatient?.state || ''}
                        onChange={handleFieldChange}
                        placeholder="UF"
                        style={{ width: '80px' }}
                      />
                      <input
                        className="mac-input"
                        name="zipCode"
                        value={draftPatient?.zipCode || ''}
                        onChange={handleFieldChange}
                        placeholder="CEP"
                        style={{ width: '140px' }}
                      />
                    </div>
                  </div>
                ) : (
                  formatFullAddress(currentPatient) || '—'
                )}
              </li>
              <li style={listItemStyle}>
                <strong>Telefone:</strong>
                {editingMain ? (
                  <input
                    className="mac-input"
                    name="phone"
                    value={draftPatient?.phone || ''}
                    onChange={handleFieldChange}
                    placeholder="Telefone"
                    style={fullWidthInputStyle}
                  />
                ) : (
                  currentPatient?.phone || '—'
                )}
              </li>
            </ul>
          </section>
        </div>
        )}
        {activeTab === 'applications' && (() => {
          const consultGhostBtn = {
            fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, letterSpacing: '-0.005em',
            padding: '7px 12px', borderRadius: 8,
            border: '1px solid #E5E5EA', background: '#FFFFFF', color: '#1C1C1E',
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap',
          };
          const consultAccentBtn = {
            fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, letterSpacing: '-0.005em',
            padding: '7px 12px', borderRadius: 8,
            border: '1px solid #1C1C1E', background: '#1C1C1E', color: '#FFFFFF',
            cursor: 'pointer', whiteSpace: 'nowrap',
          };
          const consultPrimaryBtn = {
            fontFamily: 'inherit', fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em',
            padding: '10px 16px', borderRadius: 10,
            border: '1px solid var(--button-color)', background: 'var(--button-color)', color: '#FFFFFF',
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap',
            boxShadow: '0 1px 2px rgba(var(--button-rgb),0.18), 0 6px 16px -8px rgba(var(--button-rgb),0.32)',
          };
          return (
          <div style={{ marginTop: 32 }}>

            {/* Dados complementares card */}
            <div style={{
              background: '#FFFFFF', border: '1px solid #E5E5EA', borderRadius: 22,
              padding: '22px 26px 24px',
              boxShadow: '0 1px 2px rgba(28,28,30,0.04), 0 12px 32px -22px rgba(28,28,30,0.12)',
              marginBottom: 32,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
                <div>
                  <h2 style={{ fontWeight: 700, fontSize: 18, margin: 0, letterSpacing: '-0.022em', color: '#1C1C1E' }}>
                    Dados complementares
                  </h2>
                  <div style={{ marginTop: 3, fontSize: 12.5, color: '#6E6E73', letterSpacing: '-0.005em' }}>
                    Informações clínicas básicas e marcos de acompanhamento
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {editingComplementary ? (
                    <>
                      <button type="button" onClick={() => handleCancel('complementary')} style={consultGhostBtn}>Cancelar</button>
                      <button type="button" onClick={() => handleSave('complementary')} style={consultAccentBtn}>Salvar</button>
                    </>
                  ) : (
                    <button type="button" onClick={() => setEditingComplementary(true)} style={consultGhostBtn}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5, verticalAlign: '-2px' }}>
                        <path d="M11 4H4v16h16v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Editar
                    </button>
                  )}
                </div>
              </div>

              {editingComplementary ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={complementaryRowStyle}>
                    <div style={listItemStyle}>
                      <strong>Data de Nascimento:</strong>
                      <input className="mac-input" name="birthDate" type="date" value={draftPatient?.birthDate || ''} onChange={handleFieldChange} />
                    </div>
                    <div style={listItemStyle}>
                      <strong>Sexo:</strong>
                      <select className="mac-input" name="gender" value={draftPatient?.gender || ''} onChange={handleFieldChange}>
                        <option value="">Selecione</option>
                        <option value="Masculino">Masculino</option>
                        <option value="Feminino">Feminino</option>
                      </select>
                    </div>
                    <div style={listItemStyle}>
                      <strong>Altura:</strong>
                      <input className="mac-input" name="height" type="number" value={draftPatient?.height || ''} onChange={handleFieldChange} placeholder="170" style={{ width: '120px' }} />
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '20px 16px' }}>
                  {[
                    { label: 'Idade',               value: calculateAge(currentPatient?.birthDate),                    unit: null,  positive: false },
                    { label: 'Sexo',                value: currentPatient?.gender || '—',                              unit: null,  positive: false },
                    { label: 'Altura',              value: currentPatient?.height || '—',                              unit: currentPatient?.height ? 'cm' : null, positive: false },
                    { label: 'IMC',                 value: calculateIMC(lastConsultWeight, currentPatient?.height),     unit: null,  positive: false },
                    { label: 'C.A Perdido',         value: abdominalLoss ? abdominalLoss.toFixed(1) : '—',             unit: abdominalLoss ? 'cm' : null, positive: true },
                    { label: 'Peso Inicial',        value: formatWeightValue(initialWeight) || '—',                    unit: initialWeight ? 'kg' : null, positive: false },
                    { label: 'Peso Atual',          value: formatWeightValue(lastConsultWeight) || '—',                 unit: lastConsultWeight ? 'kg' : null, positive: false },
                    { label: 'Peso Total Perdido',  value: formatWeightValue(weightLossTotal) || '—',                   unit: weightLossTotal != null ? 'kg' : null, positive: true },
                    { label: '% Peso Total Perdido',value: totalWeightLossPercent != null ? totalWeightLossPercent.toFixed(1) : '—', unit: totalWeightLossPercent != null ? '%' : null, positive: true },
                  ].map((d, i) => (
                    <div key={i} style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#8E8E93', marginBottom: 4 }}>
                        {d.label}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <span style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.028em', color: d.positive && d.value !== '—' ? '#3D8068' : '#1C1C1E', fontVariantNumeric: 'tabular-nums' }}>
                          {d.value}
                        </span>
                        {d.unit && <span style={{ fontSize: 12, fontWeight: 600, color: '#8E8E93', letterSpacing: '-0.005em' }}>{d.unit}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Consultas header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18 }}>
              <div>
                <h2 style={{ fontWeight: 700, fontSize: 26, margin: 0, letterSpacing: '-0.025em', color: '#1C1C1E' }}>Consultas</h2>
                <div style={{ marginTop: 4, fontSize: 13, color: '#6E6E73', letterSpacing: '-0.005em' }}>
                  Histórico de atendimentos, aplicações e medições
                </div>
              </div>
              <button type="button" onClick={handleNewConsultation} style={consultPrimaryBtn}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: '-2px' }}>
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Nova consulta
              </button>
            </div>

            {/* Consultas table */}
            <div style={{ background: '#FFFFFF', border: '1px solid #E5E5EA', borderRadius: 22, overflow: 'hidden', boxShadow: '0 1px 2px rgba(28,28,30,0.04), 0 12px 32px -22px rgba(28,28,30,0.12)' }}>
              {/* Header */}
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1.4fr 110px 1fr 1.1fr 148px', gap: 16, padding: '14px 22px', background: '#FAFAFB', borderBottom: '1px solid #E5E5EA', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6E6E73' }}>
                {[
                  { key: 'date', label: 'Data' },
                  { key: 'productName', label: 'Produto' },
                  { key: 'quantity', label: 'Quantidade' },
                  { key: 'abdominal', label: 'C.A' },
                  { key: 'weight', label: 'Peso Corporal' },
                ].map(({ key, label }) => (
                  <div
                    key={key}
                    onClick={() => setConsultSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'date' ? 'desc' : 'asc' })}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                  >
                    {label}
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                      <path d={consultSort.key === key && consultSort.dir === 'asc' ? 'M5 8.5v-7M5 1.5l2.5 2.5M5 1.5L2.5 4' : 'M5 1.5v7M5 8.5l2.5-2.5M5 8.5L2.5 6'} stroke={consultSort.key === key ? '#1C1C1E' : '#A8A8AE'} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                ))}
                <div style={{ textAlign: 'right' }}>Ações</div>
              </div>

              {consultationsWithLoss.length === 0 ? (
                <div style={{ padding: '40px 22px', textAlign: 'center', color: '#8E8E93', fontSize: 14 }}>
                  Nenhuma consulta registrada ainda.
                </div>
              ) : (
                consultationsWithLoss.map((item, index) => {
                  const wGood = item.weightLoss != null ? item.weightLoss > 0 : null;
                  const wFlat = item.weightLoss != null ? item.weightLoss === 0 : null;
                  const caGood = item.caLoss != null ? item.caLoss > 0 : null;
                  const caFlat = item.caLoss != null ? item.caLoss === 0 : null;
                  return (
                    <div key={`${item.date}-${index}`} style={{ display: 'grid', gridTemplateColumns: '120px 1.4fr 110px 1fr 1.1fr 148px', gap: 16, padding: '18px 22px', borderTop: index === 0 ? 'none' : '1px solid #F2F2F7', alignItems: 'center' }}>

                      {/* Data */}
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          {formatDateLabel(item.date)}
                        </div>
                        {item.time && (
                          <div style={{ fontSize: 11.5, color: '#8E8E93', marginTop: 2, letterSpacing: '-0.005em', fontVariantNumeric: 'tabular-nums' }}>
                            {item.time}
                          </div>
                        )}
                      </div>

                      {/* Produto */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(160deg, #EFE3F0 0%, #DECCDE 100%)', color: '#7A4A86', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4.5 12.5a6 6 0 018.5-8.5l7 7a6 6 0 01-8.5 8.5zM9 9l6 6" />
                          </svg>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.productName || item.product || '—'}
                          </div>
                          {item.productLab && <div style={{ fontSize: 12, color: '#8E8E93', marginTop: 2 }}>{item.productLab}</div>}
                        </div>
                      </div>

                      {/* Quantidade */}
                      <div style={{ fontSize: 14, fontWeight: 500, color: '#1C1C1E', letterSpacing: '-0.005em', whiteSpace: 'nowrap' }}>
                        {formatQuantityLabel(item.quantity, item.unit)}
                      </div>

                      {/* C.A */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.012em', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          {item.abdominal ? `${item.abdominal} cm` : '—'}
                        </div>
                        {item.caLoss != null && (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, width: 'fit-content', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: caFlat ? '#8E8E93' : (caGood ? '#3D8068' : '#9F4A60'), background: caFlat ? '#F2F2F7' : (caGood ? '#E6F1EB' : '#FBE0E5') }}>
                            {caGood ? '-' : '+'}{Math.abs(item.caLoss).toFixed(1)} cm
                            {item.caPercent != null && <span style={{ opacity: 0.7 }}>({Math.abs(item.caPercent).toFixed(1)}%)</span>}
                          </div>
                        )}
                      </div>

                      {/* Peso */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.012em', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          {item.weight ? formatWeightLabel(item.weight) : '—'}
                        </div>
                        {item.weightLoss != null && (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, width: 'fit-content', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: wFlat ? '#8E8E93' : (wGood ? '#3D8068' : '#9F4A60'), background: wFlat ? '#F2F2F7' : (wGood ? '#E6F1EB' : '#FBE0E5') }}>
                            {wGood ? '-' : '+'}{formatWeightValue(Math.abs(item.weightLoss))} kg
                            {item.percent != null && !Number.isNaN(item.percent) && <span style={{ opacity: 0.7 }}>({Math.abs(item.percent).toFixed(1)}%)</span>}
                          </div>
                        )}
                      </div>

                      {/* Ações */}
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                        {bioimpedanceReminderIds.has(item._id) && (
                          <div title="Lembrete: nova bioimpedância na próxima consulta" style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #FBEEDF', background: '#FFFBF5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MdWarning style={{ fontSize: '1rem', color: '#d97706' }} />
                          </div>
                        )}
                        {item.attachmentUrl && (
                          <button
                            title="Ver anexo"
                            style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #E5E5EA', background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                            onClick={async () => {
                              try {
                                const { data } = await api.get(`/upload/signed-url?key=${encodeURIComponent(item.attachmentUrl)}`);
                                window.open(data.url, '_blank', 'noreferrer');
                              } catch { alert('Não foi possível abrir o anexo.'); }
                            }}
                          >
                            <MdAttachFile style={{ fontSize: '1rem', color: '#7A4A86' }} />
                          </button>
                        )}
                        <button title="Editar" type="button" onClick={() => handleEditConsultation(item)} style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #E5E5EA', background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6E6E73" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4v16h16v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                        <button title="Excluir" type="button" onClick={() => handleDeleteConsultation(item)} style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #FBE0E5', background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9F4A60" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          );
        })()}
        {activeTab === 'financial' && (() => {
          const finStatusChip = (status) => {
            if (status === 'pago')     return { bg: '#E6F1EB', fg: '#3D8068', label: 'Pago',     dot: '#3D8068' };
            if (status === 'atrasado') return { bg: '#FBE0E5', fg: '#9F4A60', label: 'Atrasado', dot: '#9F4A60' };
            return { bg: '#FBEEDF', fg: '#A66730', label: 'Pendente', dot: '#D4923A' };
          };
          const totalPago = financialTransactions.filter(t => t.paymentStatus === 'pago').reduce((s, t) => s + (t.type === 'despesa' ? -t.amount : t.amount), 0);
          const totalPendente = financialTransactions.filter(t => t.paymentStatus !== 'pago').reduce((s, t) => s + (t.type === 'despesa' ? -t.amount : t.amount), 0);
          const totalGeral = totalPago + totalPendente;
          const proxPendente = financialTransactions.filter(t => t.paymentStatus !== 'pago').sort((a, b) => new Date(a.date) - new Date(b.date))[0];
          const kpiCards = [
            { label: 'Total recebido',   value: totalPago,     subtitle: `${financialTransactions.filter(t=>t.paymentStatus==='pago').length} movimentações pagas`,    bg: ['#E6F1EB','#D2E5DC'], accent: '#3D8068', icon: 'check' },
            { label: 'A receber',        value: totalPendente, subtitle: `${financialTransactions.filter(t=>t.paymentStatus!=='pago').length} movimentações pendentes`,  bg: ['#FBEEDF','#F4DCC4'], accent: '#A66730', icon: 'clock' },
            { label: 'Total no período', value: totalGeral,    subtitle: `${financialTransactions.length} movimentações registradas`,                                   bg: ['#EFE3F0','#DECCDE'], accent: '#7A4A86', icon: 'sum'   },
          ];
          return (
          <div style={{ marginTop: 32 }}>

            {/* KPI cards */}
            {financialTransactions.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 32 }}>
                {kpiCards.map((card) => (
                  <div key={card.label} style={{ background: `linear-gradient(160deg, ${card.bg[0]} 0%, ${card.bg[1]} 100%)`, borderRadius: 22, padding: '20px 22px 22px', minHeight: 132, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55), 0 1px 2px rgba(28,28,30,0.04), 0 8px 24px -16px rgba(28,28,30,0.18)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'rgba(28,28,30,0.65)' }}>{card.label}</div>
                      <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)' }}>
                        {card.icon === 'check' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={card.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 6" /></svg>}
                        {card.icon === 'clock' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={card.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>}
                        {card.icon === 'sum'   && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={card.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 4h14l-7 8 7 8H5l7-8z" /></svg>}
                      </div>
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'rgba(28,28,30,0.55)' }}>R$</span>
                        <span style={{ fontSize: 34, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.04em', color: card.accent, fontVariantNumeric: 'tabular-nums' }}>
                          {Math.abs(card.value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div style={{ marginTop: 8, fontSize: 12, color: 'rgba(28,28,30,0.6)', letterSpacing: '-0.005em' }}>{card.subtitle}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Section header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18 }}>
              <div>
                <h2 style={{ fontWeight: 700, fontSize: 26, margin: 0, letterSpacing: '-0.025em', color: '#1C1C1E' }}>Movimentação financeira</h2>
                <div style={{ marginTop: 4, fontSize: 13, color: '#6E6E73', letterSpacing: '-0.005em' }}>Histórico de cobranças, planos e pagamentos</div>
              </div>
              <button type="button" onClick={handleOpenFinancialModal} style={{ fontFamily: 'inherit', fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em', padding: '10px 16px', borderRadius: 10, border: '1px solid var(--button-color)', background: 'var(--button-color)', color: '#FFFFFF', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', boxShadow: '0 1px 2px rgba(var(--button-rgb),0.18), 0 6px 16px -8px rgba(var(--button-rgb),0.32)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: '-2px' }}>
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Nova movimentação
              </button>
            </div>

            {/* Table */}
            <div style={{ background: '#FFFFFF', border: '1px solid #E5E5EA', borderRadius: 22, overflow: 'hidden', boxShadow: '0 1px 2px rgba(28,28,30,0.04), 0 12px 32px -22px rgba(28,28,30,0.12)' }}>
              {/* Header row */}
              <div style={{ display: 'grid', gridTemplateColumns: '120px 100px 1.6fr 110px 1fr 120px 120px', gap: 16, padding: '14px 22px', background: '#FAFAFB', borderBottom: '1px solid #E5E5EA', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6E6E73', alignItems: 'center' }}>
                {['Data','Tipo','Descrição','Categoria'].map(h => (
                  <div key={h} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    {h}
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M5 1.5v7M5 8.5l2.5-2.5M5 8.5L2.5 6" stroke="#A8A8AE" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', justifyContent: 'flex-end' }}>
                  Valor
                  <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M5 1.5v7M5 8.5l2.5-2.5M5 8.5L2.5 6" stroke="#A8A8AE" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <div>Status</div>
                <div style={{ textAlign: 'right' }}>Ações</div>
              </div>

              {financialLoading ? (
                <div style={{ padding: '40px 22px', textAlign: 'center', color: '#8E8E93', fontSize: 14 }}>Carregando movimentações…</div>
              ) : financialError ? (
                <div style={{ padding: '40px 22px', textAlign: 'center', color: '#9F4A60', fontSize: 14 }}>{financialError}</div>
              ) : financialTransactions.length === 0 ? (
                <div style={{ padding: '40px 22px', textAlign: 'center', color: '#8E8E93', fontSize: 14 }}>Nenhuma movimentação financeira registrada ainda.</div>
              ) : financialTransactions.map((t, i) => {
                const isReceita = t.type !== 'despesa';
                const stat = finStatusChip(t.paymentStatus);
                return (
                  <div key={t._id} style={{ display: 'grid', gridTemplateColumns: '120px 100px 1.6fr 110px 1fr 120px 120px', gap: 16, padding: '16px 22px', borderTop: i === 0 ? 'none' : '1px solid #F2F2F7', alignItems: 'center' }}>
                    {/* Data */}
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatDate(t.date)}</div>
                    {/* Tipo */}
                    <div>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600, letterSpacing: '-0.005em', color: isReceita ? '#3D8068' : '#9F4A60', background: isReceita ? '#E6F1EB' : '#FBE0E5', padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                        <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                          {isReceita
                            ? <path d="M5 9V1M5 1l3 3M5 1L2 4" stroke="#3D8068" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" transform="rotate(180 5 5)"/>
                            : <path d="M5 1v8M5 9l3-3M5 9L2 6" stroke="#9F4A60" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>}
                        </svg>
                        {isReceita ? 'Receita' : 'Despesa'}
                      </div>
                    </div>
                    {/* Descrição */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(160deg,#EFE3F0 0%,#DECCDE 100%)', color: '#7A4A86', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="14" rx="2" /><path d="M3 10h18M7 15h4" /></svg>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.008em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description || '—'}</div>
                    </div>
                    {/* Categoria */}
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#6E6E73', letterSpacing: '-0.005em', whiteSpace: 'nowrap' }}>{t.category || '—'}</div>
                    {/* Valor */}
                    <div style={{ textAlign: 'right', fontSize: 17, fontWeight: 700, letterSpacing: '-0.025em', color: isReceita ? '#3D8068' : '#9F4A60', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {isReceita ? '+' : '−'}{formatCurrency(t.amount)}
                    </div>
                    {/* Status */}
                    <div>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600, letterSpacing: '-0.005em', color: stat.fg, background: stat.bg, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                        <span style={{ width: 5, height: 5, borderRadius: 999, background: stat.dot }} />
                        {stat.label}
                      </div>
                    </div>
                    {/* Ações */}
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                      <button title="Editar" type="button" onClick={() => handleEditTransaction(t)} style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #E5E5EA', background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6E6E73" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4v16h16v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                      </button>
                      <button title="Excluir" type="button" onClick={() => handleDeleteTransaction(t._id)} style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #FBE0E5', background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9F4A60" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /></svg>
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Footer */}
              <div style={{ padding: '14px 22px', borderTop: '1px solid #F2F2F7', background: '#FAFAFB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#6E6E73', letterSpacing: '-0.005em' }}>
                <span>{financialTransactions.length} movimentações</span>
                {proxPendente && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#8E8E93' }}>Próximo vencimento:</span>
                    <span style={{ fontWeight: 600, color: '#1C1C1E', fontVariantNumeric: 'tabular-nums' }}>{formatDate(proxPendente.date)}</span>
                    <span style={{ color: '#A66730', fontWeight: 600 }}>· {formatCurrency(proxPendente.amount)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          );
        })()}

        {showFinancialModal && (
          <div className="modal-overlay" onClick={handleCloseFinancialModal}>
            <div
              className="modal-content"
              style={{ maxWidth: '920px', width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: '28px 32px' }}
              onClick={(event) => event.stopPropagation()}
            >
              <div
                className="modal-header"
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '12px' }}
              >
                <div>
                  <h2 style={{ margin: 0 }}>Nova movimentação financeira</h2>
                  <p className="helper-text" style={{ marginTop: '4px' }}>
                    Registro vinculado ao paciente {toTitleCase(currentPatient?.name)}.
                  </p>
                </div>
                <button className="ghost-button" onClick={handleCloseFinancialModal} style={{ fontSize: '1.5rem', padding: '4px 12px' }}>
                  ×
                </button>
              </div>
              <form className="inventory-form" onSubmit={handleSubmitFinancial} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <label>
                  Paciente
                  <input type="text" className="mac-input" value={toTitleCase(currentPatient?.name) || ''} disabled />
                </label>
                <label>
                  Descrição
                  <input
                    type="text"
                    name="description"
                    className="mac-input"
                    placeholder="Ex: Consulta retorno"
                    value={financialForm.description}
                    onChange={handleFinancialFormChange}
                    required
                  />
                </label>
                <div className="form-grid inventory-grid">
                  <label>
                    Categoria
                    <select
                      name="category"
                      className="mac-input"
                      value={financialForm.category}
                      onChange={handleFinancialFormChange}
                    >
                      {revenueCategories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Data
                    <input type="date" name="date" className="mac-input" value={financialForm.date} onChange={handleFinancialFormChange} required />
                  </label>
                </div>
                {financialForm.category === 'Produto' && (
                  <>
                    <div className="form-grid inventory-grid" style={{ gridTemplateColumns: 'minmax(260px, 2fr) minmax(120px, 1fr)', alignItems: 'flex-end', gap: '12px' }}>
                      <label className="inventory-medication-field" style={{ marginBottom: 0 }}>
                        <span>Medicamento do estoque</span>
                        <div className="inventory-autocomplete">
                          <input
                            type="text"
                            className="mac-input"
                            placeholder="Digite para buscar..."
                            value={medicationQuery}
                            onChange={handleMedicationInput}
                            onFocus={() => setShowMedicationDropdown(true)}
                            onBlur={() => setTimeout(() => setShowMedicationDropdown(false), 120)}
                            autoComplete="off"
                          />
                          {showMedicationDropdown && medicationSuggestions.length > 0 && (
                            <div className="inventory-suggestions">
                              {medicationSuggestions.map((medication) => (
                                <button
                                  type="button"
                                  key={medication._id || medication.id || medication.name}
                                  className="inventory-suggestion"
                                  onMouseDown={(event) => event.preventDefault()}
                                  onClick={() => handleSelectMedication(medication)}
                                >
                                  <span className="inventory-suggestion-title">{medication.name}</span>
                                  {(medication.category || medication.laboratory) && (
                                    <small>{medication.category || medication.laboratory}</small>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {selectedMedication && (selectedMedication.laboratory || selectedMedication.category) && (
                          <small style={{ color: '#6b7280' }}>
                            {selectedMedication.laboratory || selectedMedication.category}
                          </small>
                        )}
                      </label>
                      <label>
                        Quantidade
                        <input
                          type="number"
                          min="1"
                          className="mac-input"
                          name="quantity"
                          placeholder="Quant."
                          value={financialForm.quantity}
                          onChange={handleFinancialFormChange}
                          required
                        />
                        {selectedMedication && (
                          <small style={{ color: '#6b7280' }}>
                            Disponível: {Number(selectedMedication.totalQuantity ?? 0)} {selectedMedication.unit || 'unid.'}
                          </small>
                        )}
                      </label>
                    </div>
                  </>
                )}
                <div className="form-grid inventory-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                  <label>
                    Valor (R$)
                    <input
                      type="text"
                      inputMode="numeric"
                      name="amount"
                      className="mac-input"
                      placeholder="0,00"
                      value={financialForm.amount}
                      onChange={handleAmountChange}
                      required
                    />
                  </label>
                  <label>
                    Status
                    <select name="paymentStatus" className="mac-input" value={financialForm.paymentStatus} onChange={handleFinancialFormChange}>
                      <option value="pendente">Pendente</option>
                      <option value="pago">Pago</option>
                      <option value="atrasado">Atrasado</option>
                    </select>
                  </label>
                  <label>
                    Forma de pagamento
                    <select name="paymentMethod" className="mac-input" value={financialForm.paymentMethod} onChange={handleFinancialFormChange}>
                      <option value="dinheiro">Dinheiro</option>
                      <option value="cartao_credito">Cartão de crédito</option>
                      <option value="cartao_debito">Cartão de débito</option>
                      <option value="pix">PIX</option>
                      <option value="transferencia">Transferência</option>
                      <option value="boleto">Boleto</option>
                      <option value="outro">Outro</option>
                    </select>
                  </label>
                </div>

                <div
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '16px',
                    padding: '16px',
                    background: '#f9fafb',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="hasInstallments"
                      checked={financialForm.hasInstallments}
                      onChange={handleFinancialFormChange}
                    />
                    Registrar pagamento parcelado
                  </label>
                  {financialForm.hasInstallments && (
                    <>
                      <div className="form-grid inventory-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                        <label>
                          Número de parcelas
                          <select
                            name="installments"
                            className="mac-input"
                            value={financialForm.installments}
                            onChange={handleFinancialFormChange}
                          >
                            {Array.from({ length: 12 }).map((_, index) => {
                              const count = index + 1;
                              const installmentValue = totalAmountValue / count || 0;
                              return (
                                <option key={count} value={count}>
                                  {count}x de {formatCurrency(installmentValue)}
                                </option>
                              );
                            })}
                          </select>
                        </label>
                        <label>
                          Forma de pagamento das parcelas
                          <select
                            name="installmentPaymentMethod"
                            className="mac-input"
                            value={financialForm.installmentPaymentMethod}
                            onChange={handleFinancialFormChange}
                          >
                            <option value="cartao_credito">Cartão de crédito</option>
                            <option value="boleto">Boleto</option>
                            <option value="pix">PIX</option>
                            <option value="cartao_debito">Cartão de débito</option>
                            <option value="transferencia">Transferência</option>
                            <option value="outro">Outro</option>
                          </select>
                        </label>
                      </div>
                      <div
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '12px',
                          background: '#fff',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <strong style={{ fontSize: '0.95rem' }}>Lançamentos gerados</strong>
                          <button type="button" className="ghost-button" onClick={recalculateInstallments} style={{ fontSize: '0.85rem' }}>
                            Recalcular parcelas
                          </button>
                        </div>
                        {installmentSchedule.length === 0 ? (
                          <p className="helper-text" style={{ margin: 0 }}>
                            Informe valor, data e número de parcelas para gerar o cronograma.
                          </p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {installmentSchedule.map((inst, index) => (
                              <div
                                key={`${inst.installmentNumber || index}-${index}`}
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                  gap: '8px',
                                  border: '1px solid #ede9fe',
                                  borderRadius: '12px',
                                  padding: '12px',
                                  background: '#fbfaff',
                                }}
                              >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Parcela</span>
                                  <strong>
                                    {inst.installmentNumber > 0 ? `${inst.installmentNumber}/${scheduleCount}` : 'Entrada'}
                                  </strong>
                                </div>
                                <label>
                                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Data</span>
                                  <input
                                    type="date"
                                    className="mac-input"
                                    value={inst.date}
                                    onChange={(e) => handleInstallmentFieldChange(index, 'date', e.target.value)}
                                  />
                                </label>
                                <label>
                                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Valor</span>
                                  <input
                                    type="text"
                                    className="mac-input"
                                    value={inst.amount}
                                    onChange={(e) =>
                                      handleInstallmentFieldChange(index, 'amount', formatCurrencyInput(e.target.value))
                                    }
                                  />
                                </label>
                                <label>
                                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Forma</span>
                                  <select
                                    className="mac-input"
                                    value={inst.paymentMethod}
                                    onChange={(e) => handleInstallmentFieldChange(index, 'paymentMethod', e.target.value)}
                                  >
                                    <option value="dinheiro">Dinheiro</option>
                                    <option value="pix">PIX</option>
                                    <option value="cartao_debito">Cartão de débito</option>
                                    <option value="cartao_credito">Cartão de crédito</option>
                                    <option value="transferencia">Transferência</option>
                                    <option value="boleto">Boleto</option>
                                    <option value="outro">Outro</option>
                                  </select>
                                </label>
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: Math.abs(differenceAmount) < 0.01 ? '#16a34a' : '#dc2626' }}>
                          Diferença: {formatCurrency(differenceAmount)}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <label>
                  Observações
                  <textarea
                    name="notes"
                    className="mac-input"
                    rows={3}
                    value={financialForm.notes}
                    onChange={handleFinancialFormChange}
                  />
                </label>
                <div className="modal-actions">
                  <button type="button" className="ghost-button" onClick={handleCloseFinancialModal}>
                    Cancelar
                  </button>
                  <button type="submit" className="mac-button">
                    Salvar movimentação
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {showConsultationModal && (() => {
          const prevCA = getPreviousMeasure('abdominal');
          const prevPeso = getPreviousMeasure('weight');
          const caVal = parseFloat((consultationForm.abdominal || '').replace(',', '.'));
          const pesoVal = parseFloat((consultationForm.weight || '').replace(',', '.'));
          const caDelta = !isNaN(caVal) && prevCA != null ? caVal - prevCA : null;
          const pesoDelta = !isNaN(pesoVal) && prevPeso != null ? pesoVal - prevPeso : null;
          return (
            <div
              style={{
                position: 'fixed', inset: 0, zIndex: 100,
                background: 'rgba(28,28,30,0.42)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '24px',
              }}
              onClick={closeConsultationModal}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: 720,
                  maxWidth: '95vw',
                  background: '#FFFFFF',
                  borderRadius: 22,
                  boxShadow: '0 1px 2px rgba(28,28,30,0.06), 0 40px 80px -20px rgba(28,28,30,0.32)',
                  overflow: 'hidden',
                  color: '#1C1C1E',
                }}
              >
                {/* Header */}
                <div style={{
                  padding: '18px 22px 14px',
                  borderBottom: '1px solid #F2F2F7',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  gap: 12,
                }}>
                  <div style={{ minWidth: 0 }}>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.022em', color: '#1C1C1E' }}>
                      {editingConsultationId ? 'Editar consulta' : 'Nova consulta'}
                    </h2>
                    <div style={{ marginTop: 2, fontSize: 12.5, color: '#6E6E73', letterSpacing: '-0.005em' }}>
                      {currentPatient?.name}
                    </div>
                  </div>
                  <button
                    onClick={closeConsultationModal}
                    aria-label="Fechar"
                    style={{
                      width: 30, height: 30, borderRadius: 8,
                      border: '1px solid #E5E5EA', background: '#FFFFFF', color: '#6E6E73',
                      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      padding: 0, fontFamily: 'inherit', flexShrink: 0,
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Body */}
                <form onSubmit={handleConsultationSubmit}>
                  <div style={{ padding: '16px 22px 18px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px' }}>
                    {/* LEFT */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {!editingConsultationId && showBioimpedanceAlert && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, color: '#92400e' }}>
                          <MdWarning style={{ fontSize: '1.2rem', color: '#d97706', flexShrink: 0 }} />
                          <span style={{ fontSize: '0.875rem' }}>Lembrete: esta é a consulta em que o paciente deverá realizar uma nova <strong>bioimpedância</strong>.</span>
                        </div>
                      )}

                      {/* Data e Horário */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: 10 }}>
                        <div>
                          <div style={{ marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em' }}>Data</div>
                          <input type="date" className="mac-input" name="date" value={consultationForm.date} onChange={handleConsultationChange} required style={{ background: '#FAFAFB' }} />
                        </div>
                        <div>
                          <div style={{ marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em' }}>Horário</div>
                          <input type="time" className="mac-input" name="time" value={consultationForm.time} onChange={handleConsultationChange} style={{ background: '#FAFAFB' }} />
                        </div>
                      </div>

                      {/* Produtos */}
                      <div>
                        <div style={{ marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em' }}>Produtos</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {consultationProducts.map((row) => (
                            <div key={row._rowId} style={{ display: 'grid', gridTemplateColumns: '1fr 78px 28px', gap: 6, alignItems: 'center' }}>
                              <div className="inventory-autocomplete">
                                <input
                                  type="text"
                                  className="mac-input"
                                  placeholder="Buscar produto…"
                                  value={row.query}
                                  onChange={(e) => updateProductRow(row._rowId, { query: e.target.value, selectedMedication: null })}
                                  onFocus={() => updateProductRow(row._rowId, { showDropdown: true })}
                                  onBlur={() => setTimeout(() => updateProductRow(row._rowId, { showDropdown: false }), 150)}
                                  autoComplete="off"
                                  style={{ background: '#FAFAFB' }}
                                />
                                {row.showDropdown && getProductSuggestions(row.query).length > 0 && (
                                  <div className="inventory-suggestions">
                                    {getProductSuggestions(row.query).map((med) => (
                                      <button
                                        type="button"
                                        className="inventory-suggestion"
                                        key={med._id || med.name}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => updateProductRow(row._rowId, { selectedMedication: med, query: med.name || '', showDropdown: false })}
                                      >
                                        <span className="inventory-suggestion-title">{med.name}</span>
                                        <small style={{ color: '#6b7280' }}>{med.laboratory || med.category || ''}</small>
                                      </button>
                                    ))}
                                  </div>
                                )}
                                {row.selectedMedication && (row.selectedMedication.laboratory || row.selectedMedication.category) && (
                                  <small style={{ color: '#8E8E93', marginTop: 2, display: 'block', fontSize: 11 }}>
                                    {row.selectedMedication.laboratory || row.selectedMedication.category}
                                  </small>
                                )}
                              </div>
                              <input
                                type="text"
                                className="mac-input"
                                placeholder="Qtd."
                                value={row.quantity}
                                onChange={(e) => updateProductRow(row._rowId, { quantity: e.target.value })}
                                required={Boolean(row.selectedMedication?._id)}
                                style={{ textAlign: 'center', background: '#FAFAFB' }}
                              />
                              <button
                                type="button"
                                onClick={() => removeProductRow(row._rowId)}
                                title="Remover"
                                style={{
                                  width: 28, height: 28, borderRadius: 8,
                                  border: '1px solid #E5E5EA', background: '#FFFFFF',
                                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  padding: 0, fontFamily: 'inherit',
                                  opacity: consultationProducts.length === 1 ? 0.35 : 1,
                                  pointerEvents: consultationProducts.length === 1 ? 'none' : 'auto',
                                }}
                              >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9F4A60" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={addProductRow}
                          style={{
                            marginTop: 6,
                            fontFamily: 'inherit', fontSize: 12, fontWeight: 600, letterSpacing: '-0.005em',
                            padding: '5px 10px', borderRadius: 8,
                            border: '1px dashed #D1D1D6', background: '#FFFFFF', color: '#1C1C1E',
                            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap',
                          }}
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}>
                            <path d="M12 5v14M5 12h14" />
                          </svg>
                          Adicionar produto
                        </button>
                      </div>

                      {/* C.A + Peso */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                          <div style={{ marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em' }}>
                            C.A <span style={{ fontWeight: 500, color: '#8E8E93' }}>(cm)</span>
                          </div>
                          <input
                            type="text"
                            className="mac-input"
                            name="abdominal"
                            value={consultationForm.abdominal}
                            onChange={handleConsultationChange}
                            placeholder={prevCA != null ? String(prevCA).replace('.', ',') : '—'}
                            style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, background: '#FAFAFB' }}
                          />
                          <ConsultModalDeltaRow delta={caDelta} unit="cm" prev={prevCA != null ? `${prevCA} cm` : null} />
                        </div>
                        <div>
                          <div style={{ marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em' }}>
                            Peso <span style={{ fontWeight: 500, color: '#8E8E93' }}>(kg)</span>
                          </div>
                          <input
                            type="text"
                            inputMode="decimal"
                            className="mac-input"
                            name="weight"
                            value={consultationForm.weight}
                            onChange={handleConsultationChange}
                            placeholder={prevPeso != null ? String(prevPeso).replace('.', ',') : '—'}
                            style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, background: '#FAFAFB' }}
                          />
                          <ConsultModalDeltaRow delta={pesoDelta} unit="kg" prev={prevPeso != null ? `${String(prevPeso).replace('.', ',')} kg` : null} />
                        </div>
                      </div>

                      {/* Bioimpedância file picker */}
                      <div>
                        <div style={{ marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em' }}>
                          Bioimpedância <span style={{ fontWeight: 500, color: '#8E8E93' }}>(PDF, PNG ou JPG)</span>
                        </div>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,application/pdf"
                          id="consult-attachment-input"
                          style={{ display: 'none' }}
                          onChange={(e) => setConsultationAttachmentFile(e.target.files[0] || null)}
                        />
                        <button
                          type="button"
                          onClick={() => document.getElementById('consult-attachment-input').click()}
                          style={{
                            width: '100%',
                            fontFamily: 'inherit', fontSize: 13, fontWeight: 500, letterSpacing: '-0.005em',
                            padding: '8px 10px 8px 8px', borderRadius: 10,
                            border: '1px solid #E5E5EA', background: '#FAFAFB',
                            color: consultationAttachmentFile || consultationAttachmentPreviewUrl ? '#1C1C1E' : '#6E6E73',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
                          }}
                        >
                          <span style={{
                            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: consultationAttachmentFile || consultationAttachmentPreviewUrl ? '#E6F1EB' : 'linear-gradient(160deg, #EFE3F0 0%, #DECCDE 100%)',
                            color: consultationAttachmentFile || consultationAttachmentPreviewUrl ? '#3D8068' : '#7A4A86',
                          }}>
                            {consultationAttachmentFile || consultationAttachmentPreviewUrl ? (
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 12h3l3-8 4 16 3-8h5" />
                              </svg>
                            )}
                          </span>
                          <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {consultationAttachmentFile
                              ? consultationAttachmentFile.name
                              : consultationAttachmentPreviewUrl
                                ? 'Anexo atual'
                                : 'Nenhum arquivo escolhido'}
                          </span>
                          {consultationAttachmentFile ? (
                            <span
                              onClick={(e) => { e.stopPropagation(); setConsultationAttachmentFile(null); }}
                              style={{ color: '#9F4A60', fontSize: 16, lineHeight: 1, padding: '0 6px', cursor: 'pointer' }}
                              title="Remover"
                            >×</span>
                          ) : consultationAttachmentPreviewUrl ? (
                            <a
                              href={consultationAttachmentPreviewUrl}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{ fontSize: 11.5, fontWeight: 600, padding: '4px 9px', borderRadius: 6, background: '#FFFFFF', border: '1px solid #E5E5EA', color: '#1C1C1E', flexShrink: 0, textDecoration: 'none' }}
                            >
                              Ver
                            </a>
                          ) : (
                            <span style={{ fontSize: 11.5, fontWeight: 600, padding: '4px 9px', borderRadius: 6, background: '#FFFFFF', border: '1px solid #E5E5EA', color: '#1C1C1E', flexShrink: 0 }}>
                              Escolher
                            </span>
                          )}
                        </button>
                        {consultationAttachmentPreviewUrl && (
                          <button
                            type="button"
                            onClick={() => { setConsultationAttachmentKey(null); setConsultationAttachmentPreviewUrl(null); }}
                            style={{ marginTop: 4, fontSize: 11, color: '#9F4A60', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                          >
                            Remover anexo atual
                          </button>
                        )}
                        {!consultationAttachmentFile && consultationAttachmentKey && !consultationAttachmentPreviewUrl && (
                          <small style={{ marginTop: 4, display: 'block', color: '#8E8E93' }}>Carregando anexo…</small>
                        )}
                      </div>
                    </div>

                    {/* RIGHT */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div>
                        <div style={{ marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em' }}>Informações adicionais do Paciente</div>
                        <textarea
                          className="mac-input"
                          name="notes"
                          value={consultationForm.notes}
                          onChange={handleConsultationChange}
                          rows={11}
                          placeholder="Anotações, observações clínicas, orientações verbais, queixas, intercorrências…"
                          style={{ resize: 'none', lineHeight: 1.5, fontSize: 13, padding: '10px 12px', height: 'auto', background: '#FAFAFB' }}
                        />
                      </div>
                      <div>
                        <div style={{ marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em' }}>Protocolo</div>
                        <textarea
                          className="mac-input"
                          name="protocol"
                          value={consultationForm.protocol}
                          onChange={handleConsultationChange}
                          rows={9}
                          placeholder="Ex.: Tizerpatida 10 mg semanal · próxima dose 20/05 · titulação para 12,5 mg em 30 dias…"
                          style={{ resize: 'none', lineHeight: 1.5, fontSize: 13, padding: '10px 12px', height: 'auto', background: '#FAFAFB' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div style={{
                    padding: '12px 22px',
                    borderTop: '1px solid #F2F2F7',
                    background: '#FAFAFB',
                    display: 'flex', justifyContent: 'flex-end', gap: 8,
                  }}>
                    <button type="button" onClick={closeConsultationModal} style={{
                      fontFamily: 'inherit', fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em',
                      padding: '8px 16px', borderRadius: 10,
                      border: '1px solid #E5E5EA', background: '#FFFFFF', color: '#1C1C1E',
                      cursor: 'pointer', whiteSpace: 'nowrap',
                    }}>
                      Cancelar
                    </button>
                    <button type="submit" style={{
                      fontFamily: 'inherit', fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em',
                      padding: '8px 18px', borderRadius: 10,
                      border: '1px solid #9F4A60', background: '#9F4A60', color: '#FFFFFF',
                      cursor: 'pointer', whiteSpace: 'nowrap',
                      boxShadow: '0 1px 2px rgba(159,74,96,0.18), 0 6px 16px -8px rgba(159,74,96,0.32)',
                    }}>
                      {editingConsultationId ? 'Salvar alterações' : 'Registrar consulta'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
